import styled from "styled-components";
import { useEffect, useMemo, useRef, useState } from "react";
import { Skeleton } from "antd";
import { useSelector, useDispatch } from "react-redux";
import dayjs, { Dayjs } from "dayjs";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMeasure } from "react-use";
import SwitchSelector from "react-switch-selector";
import _ from "lodash";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekday from "dayjs/plugin/weekday";

// import useSound from "use-sound";

import { imgList } from "@constants/imgList";
// import { soundList } from "@constants/soundList";
import { colors } from "@constants/colors";
import { device } from "@constants/styles";
import { InputSearchSuggest } from "@components/Inputs";
import { SelectSimple } from "@components/Selects";
import Table from "@components/Table";
import {
  dateConvert,
  getTimeArray,
  buildTimeMap,
  utcQueryStr,
} from "@utils/date";
import { apiGetReportWindow } from "@apis/reportWindow";
import DatePicker from "@components/DatePicker/DatePickerCustom";
import Excel from "@components/Excel";
import {
  ModalDeviceDetail,
  ModalFilterTemp,
  ModalFilterOperation,
  ModalFilterIot,
  ModalFilterPower,
  ModalZoomImage,
} from "@components/Modals";
import GoogleMapSM from "@components/GoogleMapSM";
import { LockIcon } from "@components/Icons";
import { useMyTranslation } from "@components/Lang";
import {
  renderWifi,
  renderLight,
  renderComp,
  renderFan,
  renderDefrost,
  renderAlarm,
  renderDoor,
} from "@components/DeviceStatus";
import {
  ChartTemperature,
  ChartNewTemperature,
  ChartOperation,
  ChartIot,
  ChartNewIot,
  ChartRssi,
  ChartNewRssi,
  ChartPower,
  ChartNewPower,
  ChartNewOperation,
  ChartNewTemperatureSum,
} from "@components/Charts";
import ChartPlaceholder from "@components/Charts/ChartPlaceholder";
import WifiEventTimeline, { toWifiEvents } from "@components/WifiEvents";
import { displayImage } from "@utils/image";
import { toFixed, toFixedOrDash } from "@utils/string";
import {
  formatDuration,
  sumOperationUsage,
  sumPowerUsage,
} from "@utils/deviceReportService";
import { runReportAggregation } from "@utils/reportTableWorkerClient";
import {
  computeStats,
  downsampleAvg,
  recommendedResolution,
} from "@utils/temperatureHistory";
import { apiGet, apiPut, apiGetS3 } from "@apis/gateway";
import { loadingAction } from "@reduxs/Actions/storeAction";
import {
  deviceDetailAction,
  filterDeviceDetailAction,
  mapLocationDevicesAction,
  updateDateAction,
  isSoundPlayingAction,
  isPlayAction,
} from "@reduxs/Actions/monitoringAction";
import { stateInterface } from "@reduxs/interface";
const { RangePicker } = DatePicker;
dayjs.extend(weekOfYear);
dayjs.extend(weekday);

// Temporarily OFF: the weeks/months charts pull a per-day file from S3, but the
// bucket/schema isn't ready on the backend yet (CORS + file shape), so every
// weeks/months view fires a report-url + S3 request that fails. With this off we
// skip the S3 fetch entirely and let the RAW MongoDB data drive the charts
// (operation/power/iot already fall back to raw; temperature falls back too via
// the resTableRaw path below). Flip back to true once the backend S3 is ready.
const USE_S3_REPORT = false;

// Report tabs: temperature, operation, power, IoT, Wi-Fi module events. Kept in
// one place because the count is needed in two spots that are easy to miss —
// the remembered-tab bounds check and the loading skeleton's row of buttons.
const REPORT_TAB_COUNT = 5;

export default function DeviceDetail() {
  const { t, currentLanguage } = useMyTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const language = useSelector(
    (state: stateInterface) => state.storage.language
  );
  const deviceDetail = useSelector(
    (state: stateInterface) => state.monitoring.deviceDetail
  );
  const filterDeviceDetail = useSelector(
    (state: stateInterface) => state.monitoring.filterDeviceDetail
  );
  const filterTemp = useSelector(
    (state: stateInterface) => state.monitoring.filterTemp
  );
  const filterOpe = useSelector(
    (state: stateInterface) => state.monitoring.filterOpe
  );
  const sound = useSelector(
    (state: stateInterface) => state?.storage?.sound ?? "off"
  );
  const isSoundPlaying = useSelector(
    (state: stateInterface) => state.monitoring.isSoundPlaying
  );
  const loading = useSelector((state: stateInterface) => state.store.loading);
  const [ref, { width }]: any = useMeasure();
  const { locationID, id } = useParams();
  const [tab, setTab] = useState(1);
  // Remember the selected metric tab (1=temp 2=operation 3=power 4=iot) per
  // device for the session, so returning from a full-report page lands back on
  // the same tab instead of resetting to temperature.
  const tabStorageKey = `deviceDetailTab:${id}`;
  const [tabReport, setTabReport] = useState<number>(() => {
    const saved = Number(sessionStorage.getItem(tabStorageKey));
    return saved >= 1 && saved <= REPORT_TAB_COUNT ? saved : 1;
  });
  useEffect(() => {
    if (id) sessionStorage.setItem(tabStorageKey, String(tabReport));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabReport, id]);
  const [plan, setPlan] = useState(1);
  const [init, setInit] = useState(false);
  const [modal, setModal] = useState({
    open: "",
  });
  const [zoomImage, setZoomImage] = useState("");
  // label is a node, not a string: a locked option carries a padlock
  const [listUnit, setListUnit] = useState<
    { value: string; label: React.ReactNode; disabled?: boolean }[]
  >(
    []
  );
  const [listPeriod, setListPeriod] = useState<
    { value: string; label: string }[]
  >([]);
  const [dataTempNew, setDataTempNew] = useState<any>({});
  const [dataTempTable, setDataTempTable] = useState<any>({});
  // Temperature-card summary stats — same math as the temperature detail
  // page/PDF (computeStats: nulls filtered out, mean divides by the actual
  // sample count, single O(n) fold). Source = the raw PER-MINUTE rows
  // (dataTempTable, already fetched for the table): for weeks/months the
  // CHART keeps the S3 per-DAY file by design (performance/cost), but stats
  // off those daily averages would smooth away the real min/max spikes and
  // skew the mean.
  const tempStatOf = (results: any[], key: string) =>
    computeStats(
      (results ?? [])
        // null/undefined/"" must be dropped BEFORE Number() — Number(null) is
        // 0, which would leak empty minutes into the stats as 0.0°C minimums
        .map((r: any) => ({
          time: r?.time ?? "",
          value:
            r?.[key] === null || r?.[key] === undefined || r?.[key] === ""
              ? NaN
              : Number(r?.[key]),
        }))
        .filter((r: any) => Number.isFinite(r.value))
    );
  const tempInsideStats = useMemo(
    () => tempStatOf(dataTempTable?.results, "insideTemp"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataTempTable]
  );
  const tempCoilStats = useMemo(
    () => tempStatOf(dataTempTable?.results, "evapCoil"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataTempTable]
  );
  // Shared pagination config for every table on this page — same treatment as
  // the metric detail pages: "แสดง 1-10 จากทั้งหมด X รายการ" caption +
  // "รายการ/หน้า" size label (the left/right layout CSS lives in
  // components/Table).
  // Resolution-aware rows for the temperature table (and the hours/days chart
  // when coarser than 1 min) — same downsampleAvg + breach itemization as the
  // temperature detail page, so this quick view and the full report agree.
  // The weekly/monthly CHART stays on the S3 per-day file by design
  // (performance/cost); stats above always use the raw rows.
  const tempResolution = Math.max(1, filterTemp?.resolution ?? 1);
  const tempDisplayRows = useMemo(
    () =>
      downsampleAvg(
        (dataTempTable?.results ?? []) as any[],
        tempResolution,
        [
          "setPointTemp",
          "lowestTemp",
          "highestTemp",
          "insideTemp",
          "realTemp",
          "evapCoil",
          "probe2R",
        ],
        {
          breachItemize: {
            keys: ["insideTemp", "realTemp"],
            minKey: "lowestTemp",
            maxKey: "highestTemp",
          },
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataTempTable, tempResolution]
  );
  const fmtPageNum = (n: number) => n.toLocaleString("en-US");
  const tablePagination = {
    showLessItems: true,
    locale: { items_per_page: t("detail.perpage") },
    showTotal: (total: number, range: [number, number]) =>
      `${t("detail.showing")} ${fmtPageNum(range[0])}-${fmtPageNum(
        range[1]
      )} ${t("detail.oftotal")} ${fmtPageNum(total)} ${t("detail.rowunit")}`,
  };
  const [dataOperationRaw, setDataOperationRaw] = useState<any>({});
  // Rows from the dedicated /wifi-event endpoint (CORNTROL-664 v2): only
  // {id, reset, time} per row. null = not fetched yet, [] = fetched and empty.
  const [dataWifiEvents, setDataWifiEvents] = useState<any>(null);
  const [dataOperationNew, setDataOperationNew] = useState<any>({});
  const [dataOperationReport, setDataOperationReport] = useState<any>({});
  const [dataPowerRaw, setDataPowerRaw] = useState<any>({});
  const [dataPowerNew, setDataPowerNew] = useState<any>({});
  const [dataPowerReport, setDataPowerReport] = useState<any>({});
  const [dataIot, setDataIot] = useState<any>({});
  const [dataIotNew, setDataIotNew] = useState<any>({});
  const [dataReportDetail, setDataReportDetail] = useState<any>({});
  // Activity log is server-paginated: dataLogsRaw holds only the CURRENT page's
  // rows, logsTotal is the backend's totalResults (for the pager), logsPage is
  // the active page. Search + range filter server-side (re-fetch page 1).
  const [dataLogsRaw, setDataLogsRaw] = useState<any[]>([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(20);
  const [logsTotal, setLogsTotal] = useState(0);
  // The active report tab's fetch came back empty-handed (see getReport).
  const [reportFailed, setReportFailed] = useState(false);
  // A month of operation data is ~52k rows (records AND second-precision
  // events) and takes the backend past the proxy's 60s ceiling, so the request
  // can only end in a 504 — see the measurement in the MR. Offer it padlocked
  // instead of letting the reader wait a minute for an error. Tied to
  // USE_S3_REPORT rather than a standing exception: once weeks/months come
  // from the pre-generated file the query is gone and so is the lock, in the
  // same commit that flips the flag. Only the operation tab now — the Wi-Fi
  // tab moved to the dedicated /wifi-event endpoint (a month is a handful of
  // rows there), and temperature, power and IoT return in time.
  const monthLocked = !USE_S3_REPORT && tabReport === 2;
  // Search suggestions. The endpoint has no distinct-values call, so the list
  // is built from the activity names already seen — page 1 alone covers the
  // recurring ones (compressor, fan, defrost) and it fills out as the user
  // pages or widens the range. Names arrive already translated by the backend.
  const [activityHints, setActivityHints] = useState<string[]>([]);
  // Every row in the current range, held only while a search term is active:
  // the endpoint ignores a search parameter, so filtering has to happen here.
  // Cleared whenever the range or language changes so it can never go stale.
  const [allLogs, setAllLogs] = useState<any[] | null>(null);
  const allLogsLoading = useRef(false);
  const [loadSound, setLoadSound] = useState(false);
  const [search, setSearch] = useState("");
  const [rangeDate, setRangeDate] = useState<any[]>([]);
  const [isStarter, setIsStarter] = useState<boolean>(true);
  // const [play, { stop }] = useSound(soundList.beeps, {
  //   loop: true,
  //   interrupt: true,
  //   onload: () => setLoadSound(true),
  // });

  const updateDate = useSelector(
    (state: stateInterface) => state.monitoring.updateDate
  );
  const options = [
    {
      label: (
        <div className="sw__choose">
          <div className={`font-inter weight-600 size-16 color-gray67e`}>
            {t("detail.report")}
          </div>
        </div>
      ),
      value: 1,
    },
    {
      label: (
        <div className="sw__choose">
          <div className={`font-inter weight-600 size-16 color-gray67e`}>
            {t("detail.history")}
          </div>
        </div>
      ),
      value: 2,
    },
  ];
  const onChangeSwitch = (newValue: any) => {
    setTab(newValue);
  };

  useEffect(() => {
    return () => {
      dispatch(deviceDetailAction({}));
    };
  }, []);

  // checkSound removed — the alarm sound is now driven globally by the
  // Layout/Default alarm registry (socket `devices` events + REST seed).
  // This page's check only watched ITS OWN device, so an alarm on any other
  // device went silent here; the registry watches the whole fleet everywhere.

  useEffect(() => {
    const intervalId = setInterval(() => {
      renderListPeriod();
    }, 60000);
    return () => clearInterval(intervalId);
  }, [filterDeviceDetail.date]);

  useEffect(() => {
    return () => {
      dispatch(deviceDetailAction({}));
    };
  }, []);

  useEffect(() => {
    return () => {
      setInit(false);
    };
  }, [id]);

  useEffect(() => {
    if (id) {
      initData();
    }
  }, [id]);
  useEffect(() => {
    // renderListUnit();
    const d = new Date();
    dispatch(
      filterDeviceDetailAction({
        ...filterDeviceDetail,
        ...{
          unit: "hours",
          days: d.getDate().toString(),
          months: (d.getMonth() + 1).toString(),
          years: d.getFullYear().toString(),
          period: d.getHours(),
          date: dayjs(),
        },
      })
    );
    // dispatch(
    //   filterDeviceDetailAction({
    //     ...filterDeviceDetail,
    //     ...{
    //       unit: "hours",
    //       days: "20",
    //       months: "10",
    //       years: "2025",
    //       period: 9,
    //       date: dayjs('2025-10-13'),
    //     },
    //   })
    // );
    setRangeDate([dayjs().subtract(1, "day"), dayjs()]);
  }, [plan]);
  useEffect(() => {
    if (init) handleFilterTemp();
  }, [filterTemp]);
  useEffect(() => {
    renderListUnit();
    renderListPeriod();
    if (init) {
      getReport(tabReport);
    }
  }, [filterDeviceDetail, init, tabReport]);
  useEffect(() => {
    renderListUnit();
  }, [currentLanguage]);
  // Arriving on a locked tab while "months" is still selected from another one
  // would fire the request the padlock exists to prevent — step back to weeks,
  // the same clamping ModalFilterTemp does when a stale resolution is too fine.
  useEffect(() => {
    if (monthLocked && filterDeviceDetail.unit === "months") {
      dispatch(
        filterDeviceDetailAction({ ...filterDeviceDetail, unit: "weeks" })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthLocked, filterDeviceDetail.unit]);
  useEffect(() => {
    dispatch(updateDateAction(dayjs()));
  }, [deviceDetail]);
  // A new range (or language) invalidates the rows the search filters over.
  useEffect(() => {
    setAllLogs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDate, currentLanguage, id]);
  // With a search term the table works off every row in the range; without one
  // it goes back to the cheap server-paginated path. Debounced either way so
  // typing doesn't fire per keystroke.
  useEffect(() => {
    if (!init || isStarter) return;
    const h = setTimeout(() => {
      if (search.trim()) loadAllLogs();
      else getDeviceLogs(1);
    }, 350);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDate, search, init, currentLanguage]);
  const initData = async () => {
    dispatch(loadingAction(true));
    const starter = await getDeviceDetail();
    // A starter device renders the plan gate and nothing else — its report
    // endpoints answer 403 anyway (the noise once seen on production), so the
    // remaining fetches would only fail loudly.
    if (!starter) {
      await getPlan();
      await getDeviceDetailReport();
    }
    setInit(true);
    dispatch(loadingAction(false));
  };
  const getReport = async (tab: number) => {
    if (isStarter) return; // gated page — nothing below it should fetch
    dispatch(loadingAction(true));
    let ok = false;
    if (tab === 1) {
      ok = await getReportTempNew();
    } else if (tab === 2) {
      ok = await getReportOperationNew();
    } else if (tab === 3) {
      ok = await getReportPowerNew();
    } else if (tab === 4) {
      ok = await getReportIotNew();
    } else if (tab === 5) {
      // Dedicated lightweight endpoint (CORNTROL-664): reset rows only, all
      // units cheap — no longer rides on the full operation report.
      ok = await getReportWifiEvents();
    }
    // Nothing arrived: say so. The fetchers leave their state untouched on
    // failure, so without this the card would keep showing whatever the
    // PREVIOUS unit loaded, and the reader has no way to tell.
    setReportFailed(!ok);
    dispatch(loadingAction(false));
  };
  const getDeviceDetail = async () => {
    // const res = await apiGet(
    //   `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device-report/detail/${locationID}/${id}`,
    //   {},
    //   language
    // );
    const res = await apiGet(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device-report/detail/${id}`,
      {},
      language
    );
    if (res.status && res.code === 200) {
      dispatch(deviceDetailAction(res?.data?.data ?? {}));
      dispatch(
        mapLocationDevicesAction([
          {
            macAddress: res?.data?.data?.deviceInfo?.macAddress ?? "",
            index1: 0,
            index2: 0,
          },
        ])
      );
      const starter =
        (res?.data?.data?.subscriptionPlan ?? "") === "starter";
      setIsStarter(starter);
      return starter;
    } else if (res.code === 403) {
      // The backend now tier-gates this endpoint itself (C11 follow-up,
      // 2026-08-17) — a starter device's detail no longer returns data to
      // read subscriptionPlan from. In practice a 403 here IS the plan gate
      // (report endpoints have 403'd starter devices all along), so it keeps
      // the explanatory page the owner chose instead of degrading to a bare
      // /404. If the backend ever 403s this endpoint for other reasons, ask
      // them for a discriminator in the body.
      setIsStarter(true);
      return true;
    } else {
      navigate("/404");
    }
    return false;
  };
  const getDeviceDetailReport = async () => {
    const res = await apiGet(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device-report/device-detail/${id}`,
      {},
      language
    );
    if (res.status && res.code === 200) {
      setDataReportDetail(res?.data?.data ?? {});
    }
  };
  // Activity-log URL for a given page/limit of the current range. No search
  // parameter: the endpoint accepts one and returns the unfiltered page
  // anyway, so the term is applied here instead (see logsFiltered).
  // The picked days are local, the endpoint reads UTC — see utcQueryStr. Sent
  // as local wall-clock, a single-day range returned 07:00 that day through
  // 06:59 the next.
  const logsUrl = (page: number, limit: number) =>
    `${
      process.env.REACT_APP_API_DOMAIN
    }/monitoring-api/device-report/${id}/pagination/activity-logs?startDate=${utcQueryStr(
      dayjs(rangeDate[0]).startOf("day")
    )}&endDate=${utcQueryStr(
      dayjs(rangeDate[1]).endOf("day")
    )}&limit=${limit}&page=${page}`;
  // What the search term is matched against: the activity name plus the two
  // columns the user can read off the row (type and who did it).
  const matchSearch = (rows: any[]) => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((e: any) =>
      [
        e?.activityName ?? "",
        e?.type ?? "",
        e?.actionBy?.user_fullname ?? "",
        e?.actionBy?.email ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  };
  const isSearching = search.trim() !== "";
  // Rows the table renders: the matches across the whole range while a term is
  // active, otherwise just the page the server returned.
  const logRows = useMemo(
    () => (isSearching ? matchSearch(allLogs ?? []) : dataLogsRaw ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSearching, allLogs, dataLogsRaw, search]
  );
  // Merge this page's activity names into the suggestion list, keeping the
  // previous array identity when nothing is new so the input doesn't re-render.
  // Names carry the reading that produced them — "อุณหภูมิอยู่ในช่วงที่ตั้งไว้ (8.9°C)" —
  // and suggesting each value would fill the list with near-duplicates that
  // each match a single row. Drop the trailing bracket: the term that is left
  // still matches every one of them.
  const collectActivityHints = (rows: any[]) => {
    setActivityHints((prev) => {
      const names = new Set(prev);
      rows.forEach((e: any) => {
        const name = String(e?.activityName ?? "")
          .replace(/\s*\([^)]*\)\s*$/, "")
          .trim();
        if (name) names.add(name);
      });
      return names.size === prev.length ? prev : Array.from(names).sort();
    });
  };
  // Server-side pagination: fetch ONLY the requested page. The pager uses
  // totalResults from the response; page size is user-selectable (≤ 100 cap).
  const getDeviceLogs = async (page = 1, pageSize = logsPageSize) => {
    dispatch(loadingAction(true));
    const res = await apiGet(logsUrl(page, pageSize), {}, language);
    if (res.status && res.code === 200) {
      const d = res?.data?.data ?? {};
      setDataLogsRaw(d?.data ?? []);
      collectActivityHints(d?.data ?? []);
      setLogsTotal(Number(d?.totalResults ?? 0));
      setLogsPage(page);
      setLogsPageSize(pageSize);
    }
    dispatch(loadingAction(false));
  };
  // Every row in the range, which the paginated view never holds: fetch page 1
  // at the max limit, read totalPages, then pull the rest in parallel. Both the
  // Excel export and the search need this; the range picker caps it at 7 days.
  const fetchAllLogs = async () => {
    const LIMIT = 100;
    const first = await apiGet(logsUrl(1, LIMIT), {}, language);
    if (!(first.status && first.code === 200)) return [];
    const fd = first?.data?.data ?? {};
    const all: any[] = [...(fd?.data ?? [])];
    const totalPages = Number(fd?.totalPages ?? 1);
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          apiGet(logsUrl(i + 2, LIMIT), {}, language)
        )
      );
      rest.forEach((r) => {
        if (r?.status && r?.code === 200)
          all.push(...(r?.data?.data?.data ?? []));
      });
    }
    // The one place that sees every row — a free chance to complete the
    // suggestion list the paginated view can only sample.
    collectActivityHints(all);
    return all;
  };
  // Load (once per range) the rows the search filters over. Guarded because the
  // debounce can fire again while the first pull is still in flight.
  const loadAllLogs = async () => {
    if (allLogs !== null || allLogsLoading.current) return;
    allLogsLoading.current = true;
    dispatch(loadingAction(true));
    const rows = await fetchAllLogs();
    setAllLogs(rows);
    dispatch(loadingAction(false));
    allLogsLoading.current = false;
  };
  // Excel export: same rows the table is showing, so the current search applies.
  const fetchAllLogsForExport = async () => {
    const all = matchSearch(allLogs ?? (await fetchAllLogs()));
    return all.map((e: any) => ({
      Time: dayjs(e?.createdAt ?? "").format("DD/MM/YYYY HH:mm:ss"),
      Activity: `${e?.activityName ?? "-"} ${e?.activityValue ?? "-"} ${
        e?.verify ?? false ? "checked" : "notchecked"
      }`,
      Type: e?.type ?? "-",
      By: renderActionBy(e),
    }));
  };

  const getPlan = async () => {
    const res = await apiGet(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device-report/plan/${id}`,
      {},
      language
    );
    if (res.status && res.code === 200) {
      setPlan((res?.data?.data?.plan ?? "standard") === "standard" ? 1 : 2);
    }
  };
  // const getReportTemp = async () => {
  //   const res = await apiGet(
  //     `${
  //       process.env.REACT_APP_API_DOMAIN
  //     }/monitoring-api/device-report/${id}/temperature?dateTime=${dayjs(
  //       filterDeviceDetail.date
  //     ).format("YYYY-MM-DD")} ${
  //       filterDeviceDetail.period.toString().length < 2
  //         ? `0${filterDeviceDetail.period}:00:00`
  //         : `${filterDeviceDetail.period}:00:00`
  //     }&dateTimeUnit=${filterDeviceDetail.unit}&tempUnit=${filterTemp.unit}`,
  //     {},
  //     language
  //   );
  //   if (res.status && res.code === 200) {
  //     setDataTemp({
  //       ...(res?.data?.data ?? {}),
  //       results: (res?.data?.data?.results ?? []).filter(
  //         (e: any) => !_.isEmpty(e?.data ?? {})
  //       ),
  //     });
  //   }
  // };

  const getReportTempNew = async () => {
    const isDailyReport =
      filterDeviceDetail.unit === "weeks" ||
      filterDeviceDetail.unit === "months";
    const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
      "YYYY-MM-DD"
    )} ${
      filterDeviceDetail.period.toString().length < 2
        ? `0${filterDeviceDetail.period}:00:00`
        : `${filterDeviceDetail.period}:00:00`
    }`;
    const rawUrlFor = (anchor: string) => `${
      process.env.REACT_APP_API_DOMAIN
    }/monitoring-api/device-report/${id}/temperature?summarize=false&dateTime=${anchor}&dateTimeUnit=${
      filterDeviceDetail.unit
    }&tempUnit=${filterTemp.unit}`;
    const chartResPromise: Promise<any> = isDailyReport
      ? (async () => {
          if (!USE_S3_REPORT) return { status: false, code: 500 };
          const resUrl = await apiGet(
            `${
              process.env.REACT_APP_API_DOMAIN
            }/monitoring-api/device-report/${id}/report-url/temperature?durationNo=${
              filterDeviceDetail.unit === "weeks"
                ? dayjs(filterDeviceDetail.date).week()
                : dayjs(filterDeviceDetail.date).month() + 1
            }&dateTimeUnit=${filterDeviceDetail.unit}&year=${dayjs(
              filterDeviceDetail.date
            ).format("YYYY")}`,
            {},
            language
          );
          if (resUrl.status && resUrl.code === 200) {
            const resS3 = await apiGetS3(`${resUrl?.data?.data}`, {});
            if (resS3.status && resS3.code === 200) {
              return {
                status: true,
                code: 200,
                data: { data: { result: resS3?.data?.data?.result } },
              };
            }
          }
          return { status: false, code: 500 };
        })()
      : apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language);
    const tableRawPromise: Promise<any> = isDailyReport
      ? apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language)
      : Promise.resolve(null);
    const [res, resTableRaw] = await Promise.all([
      chartResPromise,
      tableRawPromise,
    ]);
    // Did the raw minute data — the request everything here falls back to —
    // actually arrive? A monthly report is ~43k rows and can exceed the
    // proxy's 60s ceiling, which answers 504. Reported so the page can say so
    // instead of leaving the previous unit's data on screen.
    const rawOk = isDailyReport
      ? !!(resTableRaw?.status && resTableRaw?.code === 200)
      : !!(res?.status && res?.code === 200);
    const temp = {
      evapCoil: null,
      highestTemp: null,
      insideTemp: null,
      lowestTemp: null,
      setPointTemp: null,
      realTemp: null,
      probe2R: null,
      id: "",
      time: null,
    };
    // weeks/months raw-first fallback (mirrors operation/power/iot): the RAW
    // minute rows drive the chart via a downsampled window, so the chart is
    // correct even when the S3 per-day file is skipped/unavailable. The S3
    // block below still UPGRADES it to the official daily grid when loadable.
    if (
      isDailyReport &&
      resTableRaw?.status &&
      resTableRaw?.code === 200
    ) {
      const results = await runReportAggregation("temp", {
        rawResult: resTableRaw?.data?.data?.result ?? [],
        dateTimeStr,
        unit: filterDeviceDetail.unit,
      });
      // downsample to the range's recommended resolution so the quick-view chart
      // matches what the filter modal recommends (weeks 10m, months 30m)
      const step = recommendedResolution(filterDeviceDetail.unit);
      setDataTempNew({
        results: downsampleAvg(results as any[], step, [
          "setPointTemp",
          "lowestTemp",
          "highestTemp",
          "insideTemp",
          "realTemp",
          "evapCoil",
          "probe2R",
        ]),
      });
    }
    if (res.status && res.code === 200) {
      res["data"]["data"]["result"] = (res?.data?.data?.result ?? []).map(
        (e: any) => {
          return {
            ...e,
            time: e?.recordTime
              ? dayjs(e.recordTime).format("YYYY-MM-DD HH:mm:ss")
              : e?.time ?? "",
          };
        }
      );
      let arr: any[] = [];
      const array = getTimeArray(
        dateTimeStr,
        filterDeviceDetail.unit,
        isDailyReport ? 1440 : 1
      );
      const resultMap = buildTimeMap(res?.data?.data?.result ?? []);
      array.forEach((e) => {
        arr.push({
          ...temp,
          time: e,
          ...(resultMap.get(e) ?? {}),
        });
      });
      if (isDailyReport) {
        // The weekly/monthly report from S3 is already aggregated to one
        // row per day, so no further minute-level compression is needed.
        setDataTempNew({
          results: arr,
        });
      } else {
        let temp2 = arr;
        arr.forEach((e, i) => {
          if (i % 5 === 0 && i !== 0) {
            if (
              (temp2?.[i]?.id ?? "") !== "" &&
              (temp2?.[i - 1]?.id ?? "") === "" &&
              (temp2?.[i - 2]?.id ?? "") === "" &&
              (temp2?.[i - 3]?.id ?? "") === "" &&
              (temp2?.[i - 4]?.id ?? "") === ""
            ) {
              temp2[i - 1] = {
                ...temp2[i - 1],
                delete: true,
              };
              temp2[i - 2] = {
                ...temp2[i - 2],
                delete: true,
              };
              temp2[i - 3] = {
                ...temp2[i - 3],
                delete: true,
              };
              temp2[i - 4] = {
                ...temp2[i - 4],
                delete: true,
              };
            }
          }
        });
        arr = temp2;
        setDataTempNew({
          results: arr.filter((e) => e?.delete !== true),
        });
      }
      if (!isDailyReport) {
        setDataTempTable({
          results: arr.filter((e) => e?.delete !== true),
        });
      } else if (resTableRaw?.status && resTableRaw?.code === 200) {
        const results = await runReportAggregation("temp", {
          rawResult: resTableRaw?.data?.data?.result ?? [],
          dateTimeStr,
          unit: filterDeviceDetail.unit,
        });
        setDataTempTable({ results });
      }
    }
    return rawOk;
  };

  // const getReportOperation = async () => {
  //   const res = await apiGet(
  //     `${
  //       process.env.REACT_APP_API_DOMAIN
  //     }/monitoring-api/device-report/${id}/operation?dateTime=${dayjs(
  //       filterDeviceDetail.date
  //     ).format("YYYY-MM-DD")} ${
  //       filterDeviceDetail.period.toString().length < 2
  //         ? `0${filterDeviceDetail.period}:00:00`
  //         : `${filterDeviceDetail.period}:00:00`
  //     }&dateTimeUnit=${filterDeviceDetail.unit}`,
  //     {},
  //     language
  //   );
  //   if (res.status && res.code === 200) {
  //     setDataOperation({
  //       ...(res?.data?.data ?? {}),
  //       results: (res?.data?.data?.results ?? []).filter(
  //         (e: any) => !_.isEmpty(e?.data ?? {})
  //       ),
  //     });
  //   }
  // };
  const getReportOperationNew = async () => {
    const isDailyReport =
      filterDeviceDetail.unit === "weeks" ||
      filterDeviceDetail.unit === "months";
    const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
      "YYYY-MM-DD"
    )} ${
      filterDeviceDetail.period.toString().length < 2
        ? `0${filterDeviceDetail.period}:00:00`
        : `${filterDeviceDetail.period}:00:00`
    }`;
    const rawUrlFor = (anchor: string) => `${
      process.env.REACT_APP_API_DOMAIN
    }/monitoring-api/device-report/${id}/operation?summarize=false&dateTime=${anchor}&dateTimeUnit=${
      filterDeviceDetail.unit
    }`;
    const chartResPromise: Promise<any> = isDailyReport
      ? (async () => {
          if (!USE_S3_REPORT) return { status: false, code: 500 };
          const resUrl = await apiGet(
            `${
              process.env.REACT_APP_API_DOMAIN
            }/monitoring-api/device-report/${id}/report-url/operation?durationNo=${
              filterDeviceDetail.unit === "weeks"
                ? dayjs(filterDeviceDetail.date).week()
                : dayjs(filterDeviceDetail.date).month() + 1
            }&dateTimeUnit=${filterDeviceDetail.unit}&year=${dayjs(
              filterDeviceDetail.date
            ).format("YYYY")}`,
            {},
            language
          );
          if (resUrl.status && resUrl.code === 200) {
            const resS3 = await apiGetS3(`${resUrl?.data?.data}`, {});
            if (resS3.status && resS3.code === 200) {
              return {
                status: true,
                code: 200,
                data: { data: { result: resS3?.data?.data?.result } },
              };
            }
          }
          return { status: false, code: 500 };
        })()
      : apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language);
    const tableRawPromise: Promise<any> = isDailyReport
      ? apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language)
      : Promise.resolve(null);
    const [res, resTableRaw] = await Promise.all([
      chartResPromise,
      tableRawPromise,
    ]);
    // Did the raw minute data — the request everything here falls back to —
    // actually arrive? A monthly report is ~43k rows and can exceed the
    // proxy's 60s ceiling, which answers 504. Reported so the page can say so
    // instead of leaving the previous unit's data on screen.
    const rawOk = isDailyReport
      ? !!(resTableRaw?.status && resTableRaw?.code === 200)
      : !!(res?.status && res?.code === 200);
    const temp = {
      active: undefined,
      comp: undefined,
      defrost: undefined,
      door: undefined,
      fan: undefined,
      light: undefined,
    };
    // Rows arrive stamped with recordTime (UTC); re-stamp to local wall-clock.
    const mapTimes = (rows: any[]) =>
      (rows ?? []).map((e: any) => ({
        ...e,
        time: e?.recordTime
          ? dayjs(e.recordTime).format("YYYY-MM-DD HH:mm:ss")
          : e?.time ?? "",
      }));

    // Runtime cards summed from minute-level state diffs via the shared
    // sumOperationUsage (same math OperationDetail's summary uses). NEVER feed
    // per-day snapshot rows in here — a day whose snapshot happened to read
    // "on" would count as 24 hours of runtime.
    const computeOperationSums = (recordRows: any[], eventRows: any[]) => {
      const usage = sumOperationUsage(
        recordRows,
        eventRows,
        filterDeviceDetail.unit
      );
      setDataOperationReport({
        compressorPeriodUseInHour: `${formatDuration(usage.compressorMs)}`,
        defrostPeriodUseInHour: `${formatDuration(usage.defrostMs)}`,
        fanPeriodUseInHour: `${formatDuration(usage.fanMs)}`,
        activePeriodUseInHour: `${formatDuration(usage.activeMs)}`,
        doorOpenPeriodInHour: `${formatDuration(usage.doorOpenMs)}`,
        doorOpenTime: `${usage.doorOpenCount}`,
      });
    };

    if (!isDailyReport) {
      // hours/days — raw minute rows drive everything (unchanged behavior)
      if (res.status && res.code === 200) {
        const mapped = mapTimes(res?.data?.data?.result ?? []);
        let arrResults: any[] = [];
        const filter = mapped.filter((e: any) => e.recordType === "record");
        const filterEvent = mapped.filter((e: any) => e.recordType === "event");
        const filterUniq = _.uniqBy(filter, "time");
        const filterEventUniq = _.uniqBy(filterEvent, "time");
        const array = getTimeArray(dateTimeStr, filterDeviceDetail.unit, 1);
        const filterUniqMap = buildTimeMap(filterUniq);
        array.forEach((e) => {
          arrResults.push({
            ...temp,
            time: e,
            ...(filterUniqMap.get(e) ?? {}),
          });
        });
        let temp2 = arrResults;
        arrResults.forEach((e, i) => {
          if (i % 5 === 0 && i !== 0) {
            if (
              (temp2?.[i]?.id ?? "") !== "" &&
              (temp2?.[i - 1]?.id ?? "") === "" &&
              (temp2?.[i - 2]?.id ?? "") === "" &&
              (temp2?.[i - 3]?.id ?? "") === "" &&
              (temp2?.[i - 4]?.id ?? "") === ""
            ) {
              temp2[i - 1] = {
                ...temp2[i - 1],
                delete: true,
              };
              temp2[i - 2] = {
                ...temp2[i - 2],
                delete: true,
              };
              temp2[i - 3] = {
                ...temp2[i - 3],
                delete: true,
              };
              temp2[i - 4] = {
                ...temp2[i - 4],
                delete: true,
              };
            }
          }
        });
        arrResults = temp2;
        computeOperationSums(filterUniq, filterEventUniq);
        setDataOperationNew({
          results: arrResults.filter((e) => e?.delete !== true),
          eventResults: filterEvent,
        });
        setDataOperationRaw({
          results: filterUniq,
          eventResults: filterEventUniq,
        });
      }
      return rawOk;
    }

    // ── weekly/monthly ───────────────────────────────────────────────────
    // The RAW minute rows drive the runtime cards, the table, and a chart
    // fallback. Previously EVERYTHING hid behind the S3 per-day file's
    // success, so a failed file (e.g. the bucket missing its CORS header)
    // silently left the previous unit's data on screen — and the runtime
    // cards were summed from the per-day snapshots (24h per "on" day).
    if (resTableRaw?.status && resTableRaw?.code === 200) {
      const rawRows = mapTimes(resTableRaw?.data?.data?.result ?? []);
      const rawRecordUniq = _.uniqBy(
        rawRows.filter((e: any) => e.recordType === "record"),
        "time"
      );
      const rawEventUniq = _.uniqBy(
        rawRows.filter((e: any) => e.recordType === "event"),
        "time"
      );
      computeOperationSums(rawRecordUniq, rawEventUniq);
      const { results, eventResults } = await runReportAggregation(
        "operation",
        {
          rawResult: resTableRaw?.data?.data?.result ?? [],
          dateTimeStr,
          unit: filterDeviceDetail.unit,
        }
      );
      // Chart fallback: a 30-min/2-h grid PLUS every state-change row. Step
      // charts redraw exactly from transition points (values are constant in
      // between), so no on/off cycle is lost while the series stays light —
      // plain periodic snapshots would silently skip short cycles.
      const step = filterDeviceDetail.unit === "weeks" ? 30 : 120;
      const stateKeys = ["active", "comp", "defrost", "door", "fan", "light"];
      const changes = results.filter(
        (r: any, i: number) =>
          i > 0 && stateKeys.some((k) => r[k] !== results[i - 1][k])
      );
      const chartGrid = _.uniqBy(
        [
          ...results.filter((_e: any, i: number) => i % step === 0),
          ...changes,
        ].sort((a: any, b: any) => String(a.time).localeCompare(String(b.time))),
        "time"
      );
      setDataOperationNew({
        results: chartGrid,
        eventResults,
      });
      setDataOperationRaw({
        results: rawRecordUniq,
        eventResults: rawEventUniq,
      });
    }
    if (res.status && res.code === 200) {
      // S3 per-day file loaded — upgrade the charts to the official daily grid
      const mapped = mapTimes(res?.data?.data?.result ?? []);
      const filterEvent = mapped.filter((e: any) => e.recordType === "event");
      const filterUniqMap = buildTimeMap(
        _.uniqBy(
          mapped.filter((e: any) => e.recordType === "record"),
          "time"
        )
      );
      const array = getTimeArray(dateTimeStr, filterDeviceDetail.unit, 1440);
      const arrResults: any[] = [];
      array.forEach((e) => {
        arrResults.push({ ...temp, time: e, ...(filterUniqMap.get(e) ?? {}) });
      });
      setDataOperationNew({ results: arrResults, eventResults: filterEvent });
    }
    return rawOk;
  };
  // const getReportPower = async () => {
  //   const res = await apiGet(
  //     `${
  //       process.env.REACT_APP_API_DOMAIN
  //     }/monitoring-api/device-report/${id}/power?dateTime=${dayjs(
  //       filterDeviceDetail.date
  //     ).format("YYYY-MM-DD")} ${
  //       filterDeviceDetail.period.toString().length < 2
  //         ? `0${filterDeviceDetail.period}:00:00`
  //         : `${filterDeviceDetail.period}:00:00`
  //     }&dateTimeUnit=${filterDeviceDetail.unit}`,
  //     {},
  //     language
  //   );
  //   if (res.status && res.code === 200) {
  //     setDataPower({
  //       ...(res?.data?.data ?? {}),
  //       results: (res?.data?.data?.results ?? []).filter(
  //         (e: any) => !_.isEmpty(e?.data ?? {})
  //       ),
  //     });
  //   }
  // };
  const getReportPowerNew = async () => {
    const isDailyReport =
      filterDeviceDetail.unit === "weeks" ||
      filterDeviceDetail.unit === "months";
    const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
      "YYYY-MM-DD"
    )} ${
      filterDeviceDetail.period.toString().length < 2
        ? `0${filterDeviceDetail.period}:00:00`
        : `${filterDeviceDetail.period}:00:00`
    }`;
    const rawUrlFor = (anchor: string) => `${
      process.env.REACT_APP_API_DOMAIN
    }/monitoring-api/device-report/${id}/power?summarize=false&dateTime=${anchor}&dateTimeUnit=${
      filterDeviceDetail.unit
    }`;
    const chartResPromise: Promise<any> = isDailyReport
      ? (async () => {
          if (!USE_S3_REPORT) return { status: false, code: 500 };
          const resUrl = await apiGet(
            `${
              process.env.REACT_APP_API_DOMAIN
            }/monitoring-api/device-report/${id}/report-url/power?durationNo=${
              filterDeviceDetail.unit === "weeks"
                ? dayjs(filterDeviceDetail.date).week()
                : dayjs(filterDeviceDetail.date).month() + 1
            }&dateTimeUnit=${filterDeviceDetail.unit}&year=${dayjs(
              filterDeviceDetail.date
            ).format("YYYY")}`,
            {},
            language
          );
          if (resUrl.status && resUrl.code === 200) {
            const resS3 = await apiGetS3(`${resUrl?.data?.data}`, {});
            if (resS3.status && resS3.code === 200) {
              return {
                status: true,
                code: 200,
                data: { data: { result: resS3?.data?.data?.result } },
              };
            }
          }
          return { status: false, code: 500 };
        })()
      : apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language);
    const tableRawPromise: Promise<any> = isDailyReport
      ? apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language)
      : Promise.resolve(null);
    const [res, resTableRaw] = await Promise.all([
      chartResPromise,
      tableRawPromise,
    ]);
    // Did the raw minute data — the request everything here falls back to —
    // actually arrive? A monthly report is ~43k rows and can exceed the
    // proxy's 60s ceiling, which answers 504. Reported so the page can say so
    // instead of leaving the previous unit's data on screen.
    const rawOk = isDailyReport
      ? !!(resTableRaw?.status && resTableRaw?.code === 200)
      : !!(res?.status && res?.code === 200);
    const temp = {
      current: null,
      kw: null,
      power: null,
      voltage: null,
    };
    // Rows arrive stamped with recordTime (UTC); re-stamp to local wall-clock.
    const mapTimes = (rows: any[]) =>
      (rows ?? []).map((e: any) => ({
        ...e,
        time: e?.recordTime
          ? dayjs(e.recordTime).format("YYYY-MM-DD HH:mm:ss")
          : e?.time ?? "",
      }));

    // kWh cards via the shared sumPowerUsage (same math PowerDetail uses).
    // Minute-level rows only — see the util's note.
    const computePowerSums = (rows: any[]) => {
      setDataPowerReport({ ...sumPowerUsage(rows), mode: "normal" });
    };

    if (!isDailyReport) {
      // hours/days — raw minute rows drive everything (unchanged behavior)
      if (res.status && res.code === 200) {
        const mapped = mapTimes(res?.data?.data?.result ?? []);
        let arr: any[] = [];
        const dataUniq = _.uniqBy(mapped, "time");
        const array = getTimeArray(dateTimeStr, filterDeviceDetail.unit, 1);
        const dataUniqMap = buildTimeMap(dataUniq);
        array.forEach((e) => {
          arr.push({
            ...temp,
            time: e,
            ...(dataUniqMap.get(e) ?? {}),
          });
        });
        let temp2 = arr;
        arr.forEach((e, i) => {
          if (i % 5 === 0 && i !== 0) {
            if (
              (temp2?.[i]?.id ?? "") !== "" &&
              (temp2?.[i - 1]?.id ?? "") === "" &&
              (temp2?.[i - 2]?.id ?? "") === "" &&
              (temp2?.[i - 3]?.id ?? "") === "" &&
              (temp2?.[i - 4]?.id ?? "") === ""
            ) {
              temp2[i - 1] = {
                ...temp2[i - 1],
                delete: true,
              };
              temp2[i - 2] = {
                ...temp2[i - 2],
                delete: true,
              };
              temp2[i - 3] = {
                ...temp2[i - 3],
                delete: true,
              };
              temp2[i - 4] = {
                ...temp2[i - 4],
                delete: true,
              };
            }
          }
        });
        arr = temp2;
        computePowerSums(dataUniq);
        setDataPowerNew({
          results: arr.filter((e) => e?.delete !== true),
        });
        setDataPowerRaw({ results: dataUniq });
      }
      return rawOk;
    }

    // ── weekly/monthly ───────────────────────────────────────────────────
    // Phase 1 (owner decision): RAW minute rows drive the kWh cards, the
    // table, and a chart fallback, so the page is correct even when the S3
    // per-day file fails (bucket CORS) — and the kWh totals are integrated
    // at minute granularity, not from per-day snapshots.
    if (resTableRaw?.status && resTableRaw?.code === 200) {
      const rawUniq = _.uniqBy(
        mapTimes(resTableRaw?.data?.data?.result ?? []),
        "time"
      );
      computePowerSums(rawUniq);
      const results = await runReportAggregation("power", {
        rawResult: resTableRaw?.data?.data?.result ?? [],
        dateTimeStr,
        unit: filterDeviceDetail.unit,
      });
      // Chart fallback: numeric series are AVERAGED per 30-min/2-h window
      // (downsampleAvg: divide by the actual sample count, gaps preserved) —
      // periodic snapshots would just pick one arbitrary minute per window.
      const step = filterDeviceDetail.unit === "weeks" ? 30 : 120;
      setDataPowerNew({
        results: downsampleAvg(results as any[], step, [
          "current",
          "kw",
          "power",
          "voltage",
        ]),
      });
      setDataPowerRaw({ results: rawUniq });
    }
    if (res.status && res.code === 200) {
      // S3 per-day file loaded — upgrade the charts to the official daily grid
      const mapped = mapTimes(res?.data?.data?.result ?? []);
      const dataUniqMap = buildTimeMap(_.uniqBy(mapped, "time"));
      const array = getTimeArray(dateTimeStr, filterDeviceDetail.unit, 1440);
      const arr: any[] = [];
      array.forEach((e) => {
        arr.push({ ...temp, time: e, ...(dataUniqMap.get(e) ?? {}) });
      });
      setDataPowerNew({ results: arr });
    }
    return rawOk;
  };
  // const getReportIot = async () => {
  //   const res = await apiGet(
  //     `${
  //       process.env.REACT_APP_API_DOMAIN
  //     }/monitoring-api/device-report/${id}/iot-status?dateTime=${dayjs(
  //       filterDeviceDetail.date
  //     ).format("YYYY-MM-DD")} ${
  //       filterDeviceDetail.period.toString().length < 2
  //         ? `0${filterDeviceDetail.period}:00:00`
  //         : `${filterDeviceDetail.period}:00:00`
  //     }&dateTimeUnit=${filterDeviceDetail.unit}`,
  //     {},
  //     language
  //   );
  //   if (res.status && res.code === 200) {
  //     setDataIot({
  //       ...(res?.data?.data ?? {}),
  //       results: (res?.data?.data?.results ?? []).filter(
  //         (e: any) => !_.isEmpty(e?.data ?? {})
  //       ),
  //     });
  //   }
  // };
  // CORNTROL-664 (v2, PO-corrected): GET /device-report/:mac/wifi-event —
  // returns ONLY rows carrying a reset code ({id, reset, time}), so a month is
  // a handful of rows instead of ~43k. Same dateTime/dateTimeUnit contract as
  // the sibling reports; summarize and per-code filtering exist but the
  // timeline wants the raw list.
  const getReportWifiEvents = async () => {
    const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
      "YYYY-MM-DD"
    )} ${
      filterDeviceDetail.period.toString().length < 2
        ? `0${filterDeviceDetail.period}:00:00`
        : `${filterDeviceDetail.period}:00:00`
    }`;
    const res = await apiGetReportWindow(
      (anchor: string) =>
        `${
          process.env.REACT_APP_API_DOMAIN
        }/monitoring-api/device-report/${id}/wifi-event?dateTime=${anchor}&dateTimeUnit=${
          filterDeviceDetail.unit
        }`,
      dateTimeStr,
      filterDeviceDetail.unit,
      language
    );
    if (res.status && res.code === 200) {
      // `time` is assumed UTC like every other timestamp this backend stores
      // (recordTime, activity-log dates) and re-stamped to local wall-clock.
      // VERIFY ON FIRST TEST: the code-4 event on ตู้แช่ยาเวชภัณฑ์ (14 ส.ค.)
      // must still read 16:12:40 — if it shows a 7-hour shift, this mapping
      // is the one line to flip.
      const rows = (res?.data?.data?.result ?? []).map((e: any) => ({
        ...e,
        time: e?.time
          ? dayjs(new Date(`${String(e.time).replace(" ", "T")}Z`)).format(
              "YYYY-MM-DD HH:mm:ss"
            )
          : "",
      }));
      setDataWifiEvents(rows);
      return true;
    }
    return false;
  };
  const getReportIotNew = async () => {
    const isDailyReport =
      filterDeviceDetail.unit === "weeks" ||
      filterDeviceDetail.unit === "months";
    const dateTimeStr = `${dayjs(filterDeviceDetail.date).format(
      "YYYY-MM-DD"
    )} ${
      filterDeviceDetail.period.toString().length < 2
        ? `0${filterDeviceDetail.period}:00:00`
        : `${filterDeviceDetail.period}:00:00`
    }`;
    const rawUrlFor = (anchor: string) => `${
      process.env.REACT_APP_API_DOMAIN
    }/monitoring-api/device-report/${id}/iot-status?summarize=false&dateTime=${anchor}&dateTimeUnit=${
      filterDeviceDetail.unit
    }`;
    const chartResPromise: Promise<any> = isDailyReport
      ? (async () => {
          if (!USE_S3_REPORT) return { status: false, code: 500 };
          const resUrl = await apiGet(
            `${
              process.env.REACT_APP_API_DOMAIN
            }/monitoring-api/device-report/${id}/report-url/iot?durationNo=${
              filterDeviceDetail.unit === "weeks"
                ? dayjs(filterDeviceDetail.date).week()
                : dayjs(filterDeviceDetail.date).month() + 1
            }&dateTimeUnit=${filterDeviceDetail.unit}&year=${dayjs(
              filterDeviceDetail.date
            ).format("YYYY")}`,
            {},
            language
          );
          if (resUrl.status && resUrl.code === 200) {
            const resS3 = await apiGetS3(`${resUrl?.data?.data}`, {});
            if (resS3.status && resS3.code === 200) {
              return {
                status: true,
                code: 200,
                data: { data: { result: resS3?.data?.data?.result } },
              };
            }
          }
          return { status: false, code: 500 };
        })()
      : apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language);
    const tableRawPromise: Promise<any> = isDailyReport
      ? apiGetReportWindow(rawUrlFor, dateTimeStr, filterDeviceDetail.unit, language)
      : Promise.resolve(null);
    const [res, resTableRaw] = await Promise.all([
      chartResPromise,
      tableRawPromise,
    ]);
    // Did the raw minute data — the request everything here falls back to —
    // actually arrive? A monthly report is ~43k rows and can exceed the
    // proxy's 60s ceiling, which answers 504. Reported so the page can say so
    // instead of leaving the previous unit's data on screen.
    const rawOk = isDailyReport
      ? !!(resTableRaw?.status && resTableRaw?.code === 200)
      : !!(res?.status && res?.code === 200);
    const tempIot = {
      wifi: undefined,
      cloudStatus: undefined,
    };
    const tempRssi = {
      rssi: null,
    };
    // Rows arrive stamped with recordTime (UTC); re-stamp to local wall-clock.
    const mapTimes = (rows: any[]) =>
      (rows ?? []).map((e: any) => ({
        ...e,
        time: e?.recordTime
          ? dayjs(e.recordTime).format("YYYY-MM-DD HH:mm:ss")
          : e?.time ?? "",
      }));

    if (!isDailyReport) {
      // hours/days — raw minute rows drive everything (unchanged behavior)
      if (res.status && res.code === 200) {
        const mapped = mapTimes(res?.data?.data?.result ?? []);
        let arrWifiResults: any[] = [];
        let arrRssiResults: any[] = [];
        const array = getTimeArray(dateTimeStr, filterDeviceDetail.unit, 1);
        const resultMap = buildTimeMap(mapped);
        array.forEach((e) => {
          arrWifiResults.push({
            ...tempIot,
            time: e,
            ...(resultMap.get(e) ?? {}),
          });
          arrRssiResults.push({
            ...tempRssi,
            time: e,
            ...(resultMap.get(e) ?? {}),
          });
        });
        let temp2 = arrWifiResults;
        let temp3 = arrRssiResults;
        arrWifiResults.forEach((e, i) => {
          if (i % 5 === 0 && i !== 0) {
            if (
              (temp2?.[i]?.id ?? "") !== "" &&
              (temp2?.[i - 1]?.id ?? "") === "" &&
              (temp2?.[i - 2]?.id ?? "") === "" &&
              (temp2?.[i - 3]?.id ?? "") === "" &&
              (temp2?.[i - 4]?.id ?? "") === ""
            ) {
              temp2[i - 1] = {
                ...temp2[i - 1],
                delete: true,
              };
              temp2[i - 2] = {
                ...temp2[i - 2],
                delete: true,
              };
              temp2[i - 3] = {
                ...temp2[i - 3],
                delete: true,
              };
              temp2[i - 4] = {
                ...temp2[i - 4],
                delete: true,
              };
            }
          }
        });
        arrRssiResults.forEach((e, i) => {
          if (i % 5 === 0 && i !== 0) {
            if (
              (temp3?.[i]?.id ?? "") !== "" &&
              (temp3?.[i - 1]?.id ?? "") === "" &&
              (temp3?.[i - 2]?.id ?? "") === "" &&
              (temp3?.[i - 3]?.id ?? "") === "" &&
              (temp3?.[i - 4]?.id ?? "") === ""
            ) {
              temp3[i - 1] = {
                ...temp3[i - 1],
                delete: true,
              };
              temp3[i - 2] = {
                ...temp3[i - 2],
                delete: true,
              };
              temp3[i - 3] = {
                ...temp3[i - 3],
                delete: true,
              };
              temp3[i - 4] = {
                ...temp3[i - 4],
                delete: true,
              };
            }
          }
        });
        arrWifiResults = temp2;
        arrRssiResults = temp3;
        setDataIotNew({
          wifiResults: arrWifiResults.filter((e) => e?.delete !== true),
          rssiResults: arrRssiResults.filter((e) => e?.delete !== true),
        });
      }
      return rawOk;
    }

    // ── weekly/monthly ───────────────────────────────────────────────────
    // Phase 1 (owner decision): RAW minute rows drive the table and a chart
    // fallback, so the page is correct even when the S3 per-day file fails
    // (bucket CORS); the S3 grid below upgrades the charts when it loads.
    if (resTableRaw?.status && resTableRaw?.code === 200) {
      const { wifiResults, rssiResults } = await runReportAggregation("iot", {
        rawResult: resTableRaw?.data?.data?.result ?? [],
        dateTimeStr,
        unit: filterDeviceDetail.unit,
      });
      const step = filterDeviceDetail.unit === "weeks" ? 30 : 120;
      // wifi/cloud are on/off steps → 30-min/2-h grid PLUS every state-change
      // row, so no connect/disconnect cycle is lost (step charts redraw
      // exactly from transition points); rssi is numeric → averaged per
      // window via downsampleAvg (actual-sample-count mean, gaps preserved)
      const wifiKeys = ["wifi", "cloudStatus"];
      const wifiChanges = wifiResults.filter(
        (r: any, i: number) =>
          i > 0 && wifiKeys.some((k) => r[k] !== wifiResults[i - 1][k])
      );
      const wifiGrid = _.uniqBy(
        [
          ...wifiResults.filter((_e: any, i: number) => i % step === 0),
          ...wifiChanges,
        ].sort((a: any, b: any) => String(a.time).localeCompare(String(b.time))),
        "time"
      );
      setDataIotNew({
        wifiResults: wifiGrid,
        rssiResults: downsampleAvg(rssiResults as any[], step, ["rssi"]),
      });
    }
    if (res.status && res.code === 200) {
      // S3 per-day file loaded — upgrade the charts to the official daily grid
      const mapped = mapTimes(res?.data?.data?.result ?? []);
      const resultMap = buildTimeMap(mapped);
      const array = getTimeArray(dateTimeStr, filterDeviceDetail.unit, 1440);
      const arrWifiResults: any[] = [];
      const arrRssiResults: any[] = [];
      array.forEach((e) => {
        arrWifiResults.push({
          ...tempIot,
          time: e,
          ...(resultMap.get(e) ?? {}),
        });
        arrRssiResults.push({
          ...tempRssi,
          time: e,
          ...(resultMap.get(e) ?? {}),
        });
      });
      setDataIotNew({
        wifiResults: arrWifiResults,
        rssiResults: arrRssiResults,
      });
    }
    return rawOk;
  };
  const handleFilterTemp = async () => {
    dispatch(loadingAction(true));
    // await getReportTemp();
    await getReportTempNew();
    dispatch(loadingAction(false));
  };
  const handleFilterIot = async () => {
    dispatch(loadingAction(true));
    // await getReportIot();
    await getReportIotNew();
    dispatch(loadingAction(false));
  };
  const handleFilterPower = async () => {
    dispatch(loadingAction(true));
    // await getReportPower();
    await getReportPowerNew();
    dispatch(loadingAction(false));
  };
  const renderUpdate = (status = "") => {
    let color = "";
    let bgColor = "";
    switch (status) {
      case "normal":
        color = "color-status-normal";
        bgColor = "bg-status-normal";
        break;
      case "powerOff":
        color = "color-status-poweroff";
        bgColor = "bg-status-poweroff";
        break;
      case "offline":
        color = "color-status-offline";
        bgColor = "bg-status-offline";
        break;
      case "alarm":
        color = "color-status-alarm";
        bgColor = "bg-status-alarm";
        break;
      case "updating":
        color = "color-status-updating";
        bgColor = "bg-status-updating";
        break;
      default:
        color = "color-status-normal";
        bgColor = "bg-status-normal";
        break;
    }
    return (
      <div className={`detail__text font-sukhumvitSet-text size-16 ${color}`}>
        <div className={`detail__dot ${bgColor}`}></div>
        {t("monitoring.lastupdate")}: {dateConvert(updateDate)}
      </div>
    );
  };
  // "โดย" (actionBy) column formatter — shared by the history table and its
  // Excel export so the two never drift. Backend sends actionByType from the
  // DeviceActivityLogActionType enum (device/user/automation/protectedMode/
  // timerSystem); unknown types fall through to the raw value.
  const renderActionBy = (e: any) => {
    switch (e?.actionByType) {
      case "user":
        return (e?.actionBy?.user_fullname ?? "") !== ""
          ? e?.actionBy?.user_fullname ?? ""
          : e?.actionBy?.email ?? "-";
      case "automation":
        return "Automation";
      case "device":
        return "Device";
      case "protectedMode":
        return "Protected Mode";
      case "timerSystem":
        return t("detail.timerSystem");
      default:
        return e?.actionByType;
    }
  };
  const renderStatus = (status = "", statusText = "") => {
    let color = "";
    switch (status) {
      case "normal":
        color = "color-status-normal";
        break;
      case "powerOff":
        color = "color-status-poweroff";
        break;
      case "offline":
        color = "color-status-offline";
        break;
      case "alarm":
        color = "color-status-alarm";
        break;
      case "updating":
        color = "color-status-updating";
        break;
      default:
        color = "color-status-normal";
        break;
    }
    return (
      <div
        className={`headline__middle-text headline__middle-text--sp size-20 weight-700 font-sukhumvitSet-text ${color}`}
      >
        {t(`monitoring.${status}`)}
      </div>
    );
  };
  const renderSound = (macAddress = "", soundAlarm = false, alarm = false) => {
    if (alarm) {
      if (soundAlarm) {
        return (
          <img
            src={imgList.ico_sound}
            alt=""
            className="headline__sound headline__sound--active"
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              onMute(macAddress);
            }}
          />
        );
      } else {
        return (
          <img
            src={imgList.ico_sound_disable}
            alt=""
            className="headline__sound"
          />
        );
      }
    } else {
      return <></>;
    }
  };
  const onMute = async (macAddress = "") => {
    const params = {};
    dispatch(loadingAction(true));
    const res = await apiPut(
      `${process.env.REACT_APP_API_DOMAIN}/monitoring-api/device/${macAddress}/mute-alarm`,
      { ...params },
      {},
      language
    );
    dispatch(loadingAction(false));
  };
  const renderListUnit = () => {
    // The month option, padlocked on the two tabs that read the operation
    // report while it is still fetched live (see monthLocked).
    const monthOption = monthLocked
      ? {
          value: "months",
          disabled: true,
          label: (
            <span title={t("detail.monthlocked")}>
              {t("detail.month")}
              <LockIcon />
            </span>
          ),
        }
      : { value: "months", label: t("detail.month") };
    if (plan === 1 || plan === 2) {
      setListUnit([
        {
          value: "hours",
          label: t("detail.hour"),
        },
        {
          value: "days",
          label: t("detail.day"),
        },
        {
          value: "weeks",
          label: t("detail.week"),
        },
        monthOption,
        // {
        //   value: "years",
        //   label: "Years",
        // },
      ]);
    }
  };
  const renderListPeriod = () => {
    const d = new Date();
    let a: any = [];
    if (dayjs().isSame(filterDeviceDetail.date, "date")) {
      new Array(24).fill("").forEach((e, index) => {
        if (index <= d.getHours()) {
          a.push({
            value: index,
            label: `${
              index.toString().length < 2 ? `0${index}` : index.toString()
            }:00 - ${
              (index + 1).toString().length < 2
                ? `0${index + 1}`
                : (index + 1).toString()
            }:00`,
          });
        }
      });
    } else {
      new Array(24).fill("").forEach((e, index) => {
        a.push({
          value: index,
          label: `${
            index.toString().length < 2 ? `0${index}` : index.toString()
          }:00 - ${
            (index + 1).toString().length < 2
              ? `0${index + 1}`
              : (index + 1).toString()
          }:00`,
        });
      });
    }
    setListPeriod(a);
  };
  const renderPercent = () => {
    // const a =
    //   (deviceDetail?.deviceStatus?.electricityUnit ?? 0) /
    //   ((deviceDetail?.deviceInfo?.alarmSetting?.electricityUnitPlan ?? 0) /
    //     100);
    const a =
      parseFloat(toFixed(deviceDetail?.deviceStatus?.electricityUnit ?? 0, 1)) /
      (parseFloat(
        toFixed(
          deviceDetail?.deviceInfo?.alarmSetting?.electricityUnitPlan ?? 0,
          1
        )
      ) /
        100);
    return {
      number: isPlanElectic ? parseFloat(toFixed(a, 1)) : 0,
      string: isPlanElectic ? toFixed(a, 1) : "0",
    };
  };
  const isPlanElectic =
    (deviceDetail?.deviceInfo?.alarmSetting?.electricityUnitPlan ?? null) !==
      null &&
    (deviceDetail?.deviceInfo?.alarmSetting?.electricityUnitPlan ?? null) !== 0;
  const columns = [
    {
      title: t("detail.time"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: any, b: any) => {
        return (
          dayjs(a.createdAt, "DD/MM/YYYY HH:mm").valueOf() -
          dayjs(b.createdAt, "DD/MM/YYYY HH:mm").valueOf()
        );
      },
      render: (text: any) => {
        return <div className="color-graye6e">{text}</div>;
      },
    },
    {
      title: t("detail.activity"),
      dataIndex: "activityName",
      key: "activityName",
      sorter: (a: any, b: any) => a.activityName.length - b.activityName.length,
      render: (text: any) => {
        return (
          <div className="color-blueA4B font-sukhumvitSet-bold">{text}</div>
        );
      },
    },
    {
      title: "",
      dataIndex: "activityValue",
      key: "activityValue",
      render: (text: any) => {
        return (
          <Bagde className="color-blueaa2 font-sukhumvitSet-bold size-16">
            {text}
          </Bagde>
        );
      },
    },
    {
      title: "",
      dataIndex: "verify",
      key: "verify",
      render: (text: any) => {
        if (text) {
          return <IconCheck src={imgList.ico_check_blue} alt="" />;
        } else {
          return <></>;
        }
      },
    },
    {
      title: t("detail.type"),
      dataIndex: "type",
      key: "type",
      sorter: (a: any, b: any) => a.type.length - b.type.length,
      render: (text: any) => {
        return <div className="color-graye6e">{text}</div>;
      },
    },
    {
      title: t("detail.by"),
      dataIndex: "actionBy",
      key: "actionBy",
      sorter: (a: any, b: any) => a.actionBy.length - b.actionBy.length,
      render: (text: any) => {
        return <div className="color-graye6e">{text}</div>;
      },
    },
  ];
  // ── Starter-plan gate ──────────────────────────────────────────────────────
  // The business rule is that monitoring serves standard/professional only.
  // The list and the socket already exclude starter devices (CORNTROL-662),
  // but this page is reachable by direct URL and its detail endpoint still
  // answers — and with the socket silent, it rendered live-looking numbers
  // that never update. Gate the WHOLE page: name what is happening, keep the
  // existing subscribe link, offer the way back. The old in-content
  // ErrorStarter block below is now shadowed by this and can go whenever the
  // surrounding ternary is next refactored.
  // On isStarter ALONE, not init: isStarter is set one state-update before
  // init, and waiting for both let the headline flash the device's real
  // values for a render or two before the gate covered them. Before the fetch
  // resolves isStarter is false, so the normal loading skeleton still shows —
  // the gate simply takes over at the earliest moment the plan is known.
  if (isStarter) {
    return (
      <StarterGate>
        <div className="gate__card">
          <img src={imgList.ico_error} alt="" className="gate__icon" />
          <div className="size-18 weight-700 font-sukhumvitSet-text color-gray333">
            {t("detail.starterblocked")}
          </div>
          <div className="gate__detail size-14 font-sukhumvitSet-text color-gray67e">
            {t("detail.starterblockeddetail")}
          </div>
          <a
            href="https://corntrol.com/cart/plan"
            target="_blank"
            rel="noreferrer"
            className="gate__link color-primary weight-700 size-16 font-sukhumvitSet-text"
          >
            {t("detail.register")}
          </a>
          <button
            type="button"
            className="gate__back size-14 weight-600 font-sukhumvitSet-text"
            onClick={() => navigate("/devices")}
          >
            {t("detail.backtodevices")}
          </button>
        </div>
      </StarterGate>
    );
  }


  return (
    <Container className="detail" isTab1={tab === 1}>
      <div className="detail__head">
        <div className="detail__top">
          <div className="detail__top-head">
            <img
              src={imgList.ico_back}
              alt=""
              onClick={
                location.key !== "default"
                  ? () => navigate(-1)
                  : () => navigate("/devices")
              }
            />
            <div className="font-inter size-30 color-blue828 weight-600">
              {t("detail.head")}
            </div>
          </div>
          {/* {renderUpdate(deviceDetail?.deviceStatus?.status ?? "")} */}
        </div>
        {/* Breadcrumb and last-updated share one row: both are secondary info,
            so they read as a single tier under the title — and the timestamp
            fills the space that was left empty to the right of the breadcrumb. */}
        <div className="detail__breadclumb">
          <div
            className="font-sukhumvitSet-text size-14 color-black pointer"
            onClick={() => navigate(`/devices`)}
          >
            {t("devices.head")}
          </div>
          <div className="sep">/</div>
          {/* the device name, not "รายละเอียดอุปกรณ์": it already appears as the
              page title, and the metric pages one level down use the device name
              in this position — so the trail stays continuous as you go deeper */}
          <div className="font-sukhumvitSet-text size-14 active">
            {deviceDetail?.deviceInfo?.name ?? ""}
          </div>
          <div className="detail__lastupdate font-sukhumvitSet-text size-14 color-gray474">
            <img src={imgList.ico_clock} alt="" />
            {t("monitoring.lastupdate")}:{" "}
            <span className="color-black">{dateConvert(updateDate)}</span>
          </div>
        </div>
        {(deviceDetail?.controlInfo?.errors ?? []).map(
          (e: any, key: number) => {
            return (
              <div className="detail__alert" key={key}>
                <img
                  src={imgList.ico_danger}
                  alt=""
                  className="detail__alert-left"
                />
                <div className="detail__alert-middle size-12 weight-600 font-sukhumvitSet-text color-white">
                  {e?.code ?? ""} :
                </div>
                <div className="detail__alert-right size-12 font-sukhumvitSet-text color-white">
                  {e?.description ?? ""}
                </div>
              </div>
            );
          }
        )}
        <Headline className="headline">
          <div className="headline__left">
            <div className="headline__cover">
              <img
                src={
                  deviceDetail?.deviceInfo?.image ?? ""
                    ? displayImage(deviceDetail?.deviceInfo?.image ?? "")
                    : imgList.ex
                }
                alt=""
              />
            </div>
            <div className="headline__left-detail">
              <div className="headline__row-head">
                <div className="headline__name size-16 weight-600 font-sukhumvitSet-text color-black">
                  {deviceDetail?.deviceInfo?.name ?? ""}
                </div>
                {renderSound(
                  deviceDetail?.controlInfo?.macAddress ?? "",
                  deviceDetail?.controlInfo?.soundAlarm ?? false,
                  deviceDetail?.controlInfo?.control?.alarm ?? false
                )}
              </div>
              <div className="headline__text-group">
                <div className="headline__text headline__text--sp">
                  <img src={imgList.ico_location_gray} alt="" />
                  <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                    {deviceDetail?.name ?? ""}
                  </div>
                </div>
              </div>
              {/* <div className="headline__text-group">
                <div className="headline__text">
                  <img src={imgList.ico_build_gray} alt="" />
                  <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                    โซนออฟฟิศ
                  </div>
                </div>
                <div className="headline__text">
                  <img src={imgList.ico_folder_fray} alt="" />
                  <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                    ห้องเย็น
                  </div>
                </div>
              </div> */}
              <div className="headline__text-group">
                <div className="headline__text headline__text--sp">
                  <img src={imgList.ico_user_gray} alt="" />
                  <div className="color-grayf4f size-12 font-sukhumvitSet-text">
                    {deviceDetail?.isOwner ?? false
                      ? t("monitoring.mydevice")
                      : t("monitoring.sharedevice")}
                  </div>
                </div>
              </div>
              <div
                className="headline__btn"
                onClick={() => setModal({ open: "device_detail" })}
              >
                {/* named after what it opens, and the modal's heading says the
                    same words back — "ดูเพิ่มเติม" made the reader click to
                    find out what "more" meant. Its own key, not the generic
                    `seemore`, which MetricDetailLayout still offers. */}
                <div className="size-12 weight-600 font-sukhumvitSet-text color-black">
                  {t("detail.deviceinfobtn")}
                </div>
                <img src={imgList.ico_arrow_yellow} alt="" />
              </div>
            </div>
            {/* <div className="headline__hr-top" /> */}
          </div>
          <div className="headline__map-warp">
            <div className="headline__map">
              {/* <div className="headline__hr-bottom" /> */}
              <GoogleMapSM
                lat={dataReportDetail?.coordinates?.[1] ?? 0}
                lng={dataReportDetail?.coordinates?.[0] ?? 0}
                googlePlaceId={dataReportDetail?.googlePlaceId ?? ""}
              />
            </div>
          </div>
          <div className="headline__middle">
            <div className="headline__temp">
              <div className="size-14 weight-500 font-sukhumvitSet-text color-grayf4f">
                {t(
                  (deviceDetail?.deviceStatus?.status ?? "") === "offline"
                    ? "monitoring.lasttemp"
                    : "monitoring.currenttemp"
                )}
              </div>
              <div className="size-36 weight-600 font-sukhumvitSet-text color-gray333">
                {toFixedOrDash(deviceDetail?.deviceInfo?.currentTemp, 1)}
                {toFixedOrDash(deviceDetail?.deviceInfo?.currentTemp, 1) !==
                  "-" && <span className="size-16">°C</span>}
              </div>
            </div>
            <div className="headline__group">
              {renderWifi(
                deviceDetail?.controlInfo?.control?.wifi ?? false,
                deviceDetail?.controlInfo?.rssi ?? null,
                deviceDetail?.controlInfo?.wifiStrength ?? "good"
              )}
              {renderLight(deviceDetail?.controlInfo?.control?.light ?? false)}
              {renderDefrost(
                deviceDetail?.deviceStatus?.status ?? "",
                deviceDetail?.controlInfo?.control?.defrost ?? false,
                deviceDetail?.controlInfo?.flashing?.defrost ?? false
              )}
              {renderFan(
                deviceDetail?.deviceStatus?.status ?? "",
                deviceDetail?.controlInfo?.control?.fan ?? false,
                deviceDetail?.controlInfo?.flashing?.fan ?? false
              )}
              {renderComp(
                deviceDetail?.deviceStatus?.status ?? "",
                deviceDetail?.controlInfo?.control?.comp ?? false,
                deviceDetail?.controlInfo?.flashing?.comp ?? false
              )}
              {renderAlarm(
                deviceDetail?.controlInfo?.control?.alarm ?? false,
                deviceDetail?.controlInfo?.flashing?.alarm ?? false
              )}
              {renderDoor(
                deviceDetail?.deviceStatus?.status ?? "",
                deviceDetail?.controlInfo?.control?.door ?? false,
                deviceDetail?.controlInfo?.flashing?.door ?? false
              )}
            </div>
            <div className="headline__middle-group">
              <div className="headline__middle-left">
                <div className="headline__middle-text size-12 font-sukhumvitSet-text color-grayd7d">
                  {t("monitoring.status")}
                </div>
                {renderStatus(
                  deviceDetail?.deviceStatus?.status ?? "",
                  deviceDetail?.deviceStatus?.statusDisplayText ?? ""
                )}
              </div>
              <div className="headline__middle-right">
                <div className="headline__middle-text size-12 font-sukhumvitSet-text color-grayd7d">
                  {t("monitoring.mode")} :{" "}
                  <span className="color-black">
                    {t(`monitoring.${deviceDetail?.deviceStatus?.mode ?? ""}`)}
                  </span>
                </div>
                <div className="headline__middle-text size-12 font-sukhumvitSet-text color-grayd7d">
                  {t("monitoring.vol")} :{" "}
                  <span className="color-black">
                    {deviceDetail?.deviceStatus?.voltage ?? ""}V
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="headline__hr" />
          <div className="headline__right" ref={ref}>
            <Progress
              className="progress"
              width={width}
              percent={isPlanElectic ? renderPercent().number : 0}
              spColor={
                !isPlanElectic
                  ? false
                  : renderPercent().number > 100
                  ? true
                  : false
              }
            >
              <div className="progress__progress">
                <div className="progress__bar-overflow">
                  <div className="progress__bar" />
                  <div className="progress__detail">
                    <div className="progress__avg size-12 font-sukhumvitSet-text color-grayf4f">
                      {t("monitoring.avg")}
                    </div>
                    <div className="progress__percent size-30 weight-600 font-sukhumvitSet-text color-blue828">
                      {isPlanElectic ? renderPercent().string : "-"}%
                    </div>
                    <div className="size-12 weight-500 font-sukhumvitSet-text color-gray282">
                      {t("detail.remain")}{" "}
                      {isPlanElectic
                        ? toFixed(
                            parseFloat(
                              toFixed(
                                deviceDetail?.deviceInfo?.alarmSetting
                                  ?.electricityUnitPlan ?? 0,
                                1
                              )
                            ) -
                              parseFloat(
                                toFixed(
                                  deviceDetail?.deviceStatus?.electricityUnit ??
                                    0,
                                  1
                                )
                              ),
                            1
                          )
                        : "-"}
                      {` ${t("monitoring.unit")}`}
                    </div>
                  </div>
                </div>
                <div className="progress__row">
                  <div className="progress__row-detail">
                    <div className="progress__dot progress__dot--1" />
                    <div className="progress__row-text text-left">
                      <div className="size-12 weight-500 font-sukhumvitSet-text color-grayf4f">
                        {t("detail.used")}
                      </div>
                      <div className="size-26 weight-700 font-sukhumvitSet-text color-gray333">
                        {isPlanElectic
                          ? toFixed(
                              deviceDetail?.deviceStatus?.electricityUnit ?? 0,
                              1
                            )
                          : "-"}{" "}
                        <span className="size-16">{t("monitoring.unit")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="progress__row-detail progress__row-detail--2">
                    <div className="progress__dot progress__dot--2" />
                    <div className="progress__row-text text-right">
                      <div className="size-12 weight-500 font-sukhumvitSet-text color-grayf4f">
                        {t("detail.electplan")}
                      </div>
                      <div className="size-26 weight-700 font-sukhumvitSet-text color-gray333">
                        {isPlanElectic
                          ? toFixed(
                              deviceDetail?.deviceInfo?.alarmSetting
                                ?.electricityUnitPlan ?? 0,
                              1
                            )
                          : "-"}{" "}
                        <span className="size-16">{t("monitoring.unit")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Progress>
          </div>
        </Headline>
        <div className="detail__filter">
          <div className="detail__filter-left">
            <div className="detail__switch">
              <SwitchWarp className="sw">
                <SwitchSelector
                  forcedSelectedIndex={options.findIndex(
                    ({ value }) => value === tab
                  )}
                  onChange={onChangeSwitch}
                  options={options}
                  backgroundColor={"#353b48"}
                  fontColor={"#f5f6fa"}
                />
              </SwitchWarp>
            </div>
            {tab === 1 && (
              <>
                <div className="detail__period">
                  <SelectSimple
                    disabled={isStarter}
                    id="unit"
                    labelText={t("detail.unit")}
                    value={filterDeviceDetail.unit}
                    options={listUnit}
                    placeholder={t("monitoring.all")}
                    classNameWarp=""
                    onChange={(e) => {
                      if (e === "weeks") {
                        dispatch(
                          filterDeviceDetailAction({
                            ...filterDeviceDetail,
                            unit: e,
                            date: dayjs().subtract(1, "day"),
                          })
                        );
                      } else {
                        dispatch(
                          filterDeviceDetailAction({
                            ...filterDeviceDetail,
                            unit: e,
                          })
                        );
                      }
                    }}
                  />
                </div>
                <div className="detail__period">
                  <DatePick id="date-search">
                    <TextDatePick disabled={isStarter}>
                      {/* {t("detail.perioddate")} */}
                      {t(`filter.${filterDeviceDetail.unit}`)}
                    </TextDatePick>
                    <DatePicker
                      disabled={isStarter}
                      allowClear={false}
                      value={filterDeviceDetail.date}
                      picker={
                        filterDeviceDetail.unit === "hours" ||
                        filterDeviceDetail.unit === "days"
                          ? "date"
                          : filterDeviceDetail.unit === "weeks"
                          ? "week"
                          : "month"
                      }
                      format={
                        filterDeviceDetail.unit === "hours" ||
                        filterDeviceDetail.unit === "days"
                          ? "DD/MM/YYYY"
                          : filterDeviceDetail.unit === "weeks"
                          ? "YYYY-wo"
                          : "MM/YYYY"
                      }
                      disabledDate={(current) => {
                        if (filterDeviceDetail.unit === "weeks") {
                          return (
                            current > dayjs().subtract(1, "day") ||
                            current > dayjs() ||
                            current <
                              dayjs().subtract(plan === 1 ? 3 : 12, "month")
                          );
                        } else {
                          return (
                            current > dayjs() ||
                            current <
                              dayjs().subtract(plan === 1 ? 3 : 12, "month")
                          );
                        }
                      }}
                      getPopupContainer={() =>
                        document.getElementById("date-search") as HTMLElement
                      }
                      onChange={(e) => {
                        const d = new Date();
                        let period = filterDeviceDetail.period;
                        if (
                          dayjs().isSame(e, "date") &&
                          filterDeviceDetail.period > d.getHours()
                        ) {
                          period = d.getHours();
                        }
                        dispatch(
                          filterDeviceDetailAction({
                            ...filterDeviceDetail,
                            date: e,
                            period: period,
                          })
                        );
                      }}
                    />
                  </DatePick>
                </div>
                <div className="detail__period">
                  <SelectSimple
                    id="period"
                    labelText={t("detail.periodtime")}
                    value={filterDeviceDetail.period}
                    options={listPeriod}
                    placeholder={t("monitoring.all")}
                    classNameWarp=""
                    disabled={
                      filterDeviceDetail.unit === "hours" && !isStarter
                        ? false
                        : true
                    }
                    onChange={(e) => {
                      dispatch(
                        filterDeviceDetailAction({
                          ...filterDeviceDetail,
                          period: e,
                        })
                      );
                    }}
                  />
                </div>
              </>
            )}
          </div>
          {tab === 2 && (
            <div className="detail__filter-right detail__filter-right--logs">
              <Excel
                disabled={isStarter}
                fileName={`${id}-logs-${dayjs().format("YYYYMMDDHHmmss")}`}
                header={
                  [
                    t("detail.time"),
                    t("detail.activity"),
                    t("detail.type"),
                    t("detail.by"),
                  ] as any
                }
                getData={fetchAllLogsForExport as any}
                button={
                  <div
                    className={`detail__download${
                      isStarter ? " detail__download--disabled" : ""
                    }`}
                  >
                    <img src={imgList.ico_download} alt="" />
                  </div>
                }
              />
              <div className="detail__search detail__search--desktop">
                <InputSearchSuggest
                  disabled={isStarter}
                  value={search}
                  suggestions={activityHints}
                  placeholder={t("detail.search")}
                  onChangeText={setSearch}
                />
              </div>
              <div className="detail__date detail__date--desktop">
                <DatePick id="date-search">
                  <RangePicker
                    disabled={isStarter}
                    value={[rangeDate[0], rangeDate[1]]}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => {
                      return (
                        current > dayjs() ||
                        current < dayjs().subtract(7, "day")
                      );
                    }}
                    getPopupContainer={() =>
                      document.getElementById("date-search") as HTMLElement
                    }
                    allowClear={false}
                    onChange={(e) => setRangeDate([e?.[0], e?.[1]])}
                  />
                </DatePick>
              </div>
            </div>
          )}
        </div>
        <div
          className={`detail__filter detail__filter--ismobile ${
            tab === 2 ? "show" : ""
          }`}
        >
          <div className="detail__filter-left">
            <div className="detail__search">
              <InputSearchSuggest
                disabled={isStarter}
                value={search}
                suggestions={activityHints}
                placeholder={t("detail.search")}
                onChangeText={setSearch}
              />
            </div>
            <div className="detail__date">
              <DatePick id="date-search">
                <RangePicker
                  value={[rangeDate[0], rangeDate[1]]}
                  format="DD/MM/YYYY"
                  disabledDate={(current) => {
                    return (
                      current > dayjs() || current < dayjs().subtract(7, "day")
                    );
                  }}
                  getPopupContainer={() =>
                    document.getElementById("date-search") as HTMLElement
                  }
                  allowClear={false}
                  onChange={(e) => setRangeDate([e?.[0], e?.[1]])}
                />
              </DatePick>
            </div>
          </div>
        </div>
      </div>
      {init ? (
        <div className="detail__content">
          {isStarter ? (
            <ErrorStarter className="text-center error">
              <img src={imgList.ico_error} alt="" className="error__image" />
              <div className="size-16 color-gray797 weight-700">
                {t("detail.buy")}
              </div>
              <div className="size-16 color-gray797 weight-700 error__txt-last">
                {t("detail.etc")}
              </div>
              <div className="error__link">
                <img src={imgList.ico_plus_yellow} alt="" />
                <a
                  href="https://corntrol.com/cart/plan"
                  target="_blank"
                  className="color-primary weight-700 size-16"
                >
                  {t("detail.register")}
                </a>
              </div>
            </ErrorStarter>
          ) : (
            <>
              {tab === 1 && (
                <Tab className="tab">
                  <div
                    className={`tab__btn${
                      tabReport === 1 ? " tab__btn--active" : ""
                    }`}
                    onClick={() => setTabReport(1)}
                  >
                    <img
                      src={
                        imgList[
                          `btn_temp_report_${
                            tabReport === 1 ? "active" : "disable"
                          }`
                        ]
                      }
                      alt=""
                    />
                    <div
                      className={`weight-500 ${
                        tabReport === 1
                          ? "color-white size-12"
                          : "color-gray797 size-10"
                      }`}
                    >
                      {t("detail.temp")}
                    </div>
                  </div>
                  <div
                    className={`tab__btn${
                      tabReport === 2 ? " tab__btn--active" : ""
                    }`}
                    onClick={() => setTabReport(2)}
                  >
                    <img
                      src={
                        imgList[
                          `btn_operation_report_${
                            tabReport === 2 ? "active" : "disable"
                          }`
                        ]
                      }
                      alt=""
                    />
                    <div
                      className={`weight-500 ${
                        tabReport === 2
                          ? "color-white size-12"
                          : "color-gray797 size-10"
                      }`}
                    >
                      {t("detail.ope")}
                    </div>
                  </div>
                  <div
                    className={`tab__btn${
                      tabReport === 3 ? " tab__btn--active" : ""
                    }`}
                    onClick={() => setTabReport(3)}
                  >
                    <img
                      src={
                        imgList[
                          `btn_power_report_${
                            tabReport === 3 ? "active" : "disable"
                          }`
                        ]
                      }
                      alt=""
                    />
                    <div
                      className={`weight-500 ${
                        tabReport === 3
                          ? "color-white size-12"
                          : "color-gray797 size-10"
                      }`}
                    >
                      {t("detail.power")}
                    </div>
                  </div>
                  <div
                    className={`tab__btn${
                      tabReport === 4 ? " tab__btn--active" : ""
                    }`}
                    onClick={() => setTabReport(4)}
                  >
                    <img
                      src={
                        imgList[
                          `btn_iot_report_${
                            tabReport === 4 ? "active" : "disable"
                          }`
                        ]
                      }
                      alt=""
                    />
                    <div
                      className={`weight-500 ${
                        tabReport === 4
                          ? "color-white size-12"
                          : "color-gray797 size-10"
                      }`}
                    >
                      {t("detail.iot")}
                    </div>
                  </div>
                  <div
                    className={`tab__btn${
                      tabReport === 5 ? " tab__btn--active" : ""
                    }`}
                    onClick={() => setTabReport(5)}
                  >
                    <img
                      src={
                        imgList[
                          `btn_wifievent_report_${
                            tabReport === 5 ? "active" : "disable"
                          }`
                        ]
                      }
                      alt=""
                    />
                    <div
                      className={`weight-500 ${
                        tabReport === 5
                          ? "color-white size-12"
                          : "color-gray797 size-10"
                      }`}
                    >
                      {t("detail.wifievent")}
                    </div>
                  </div>
                </Tab>
              )}
              {tab === 1 && reportFailed ? (
                // stands in for whichever metric card would have rendered —
                // the fetchers leave their state alone on failure, so showing
                // the card would show the PREVIOUS unit's numbers under the
                // new unit's heading
                <ReportError onRetry={() => getReport(tabReport)} t={t} />
              ) : tab === 1 ? (
                <>
                  {tabReport === 1 && (
                    <div className="detail__card detail__card--margin">
                      <div className="detail__card-head">
                        <div className="detail__card-name">
                          <img src={imgList.ico_temperature} alt="" />
                          <div className="size-20 weight-700 font-sukhumvitSet-text color-black">
                            {t("detail.temp")}
                          </div>
                        </div>
                        <div className="detail__card-filter">
                          <img
                            src={imgList.ico_filter}
                            alt=""
                            className="w-50"
                            onClick={() => setModal({ open: "filter_temp" })}
                          />
                          {/* labeled pill instead of a bare arrow — customers
                              looking for the PDF/Excel downloads need the scent
                              trail to the full temperature report page */}
                          <div
                            className="detail__card-report last"
                            onClick={() =>
                              navigate(`/device-detail/${id}/temperature`)
                            }
                          >
                            <div className="size-12 weight-600 font-sukhumvitSet-text color-black">
                              {t("detail.reportdownloadbtn")}
                            </div>
                            <img src={imgList.ico_arrow_yellow} alt="" />
                          </div>
                        </div>
                      </div>
                      <TempBox>
                        <Highlight2 className="high temp">
                          <div className="size-16 weight-400 color-gray030 high__title">
                            {t("detail.roomtempsum")}
                          </div>
                          <div className="high__box">
                            <img
                              src={imgList.ico_low}
                              alt=""
                              className="high__img"
                            />
                            <div className="high__detail">
                              <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                                {tempInsideStats.count > 0 ? (
                                  <>
                                    {toFixed(tempInsideStats.min ?? 0, 1)}
                                    <span className="size-12 font-sukhumvitSet-text color-black">
                                      °{filterTemp.unit === "c" ? "C" : "F"}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </div>
                              <div className="size-16 font-sukhumvitSet-text color-graye6e">
                                {t("detail.lowest")}
                              </div>
                            </div>
                          </div>
                          <div className="high__box">
                            <img
                              src={imgList.ico_high}
                              alt=""
                              className="high__img"
                            />
                            <div className="high__detail">
                              <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                                {tempInsideStats.count > 0 ? (
                                  <>
                                    {toFixed(tempInsideStats.max ?? 0, 1)}
                                    <span className="size-12 font-sukhumvitSet-text color-black">
                                      °{filterTemp.unit === "c" ? "C" : "F"}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </div>
                              <div className="size-16 font-sukhumvitSet-text color-graye6e">
                                {t("detail.highest")}
                              </div>
                            </div>
                          </div>
                          <div className="high__box">
                            <img
                              src={imgList.ico_avg}
                              alt=""
                              className="high__img"
                            />
                            <div className="high__detail">
                              <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                                {tempInsideStats.count > 0 ? (
                                  <>
                                    {toFixed(tempInsideStats.avg ?? 0, 1)}
                                    <span className="size-12 font-sukhumvitSet-text color-black">
                                      °{filterTemp.unit === "c" ? "C" : "F"}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </div>
                              <div className="size-16 font-sukhumvitSet-text color-graye6e">
                                {t("detail.average")}
                              </div>
                            </div>
                          </div>
                        </Highlight2>
                        <Highlight2 className="high temp">
                          <div className="size-16 weight-400 color-gray030 high__title">
                            {t("detail.evaptempsum")}
                          </div>
                          <div className="high__box">
                            <img
                              src={imgList.ico_low}
                              alt=""
                              className="high__img"
                            />
                            <div className="high__detail">
                              <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                                {tempCoilStats.count > 0 ? (
                                  <>
                                    {toFixed(tempCoilStats.min ?? 0, 1)}
                                    <span className="size-12 font-sukhumvitSet-text color-black">
                                      °{filterTemp.unit === "c" ? "C" : "F"}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </div>
                              <div className="size-16 font-sukhumvitSet-text color-graye6e">
                                {t("detail.lowest")}
                              </div>
                            </div>
                          </div>
                          <div className="high__box">
                            <img
                              src={imgList.ico_high}
                              alt=""
                              className="high__img"
                            />
                            <div className="high__detail">
                              <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                                {tempCoilStats.count > 0 ? (
                                  <>
                                    {toFixed(tempCoilStats.max ?? 0, 1)}
                                    <span className="size-12 font-sukhumvitSet-text color-black">
                                      °{filterTemp.unit === "c" ? "C" : "F"}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </div>
                              <div className="size-16 font-sukhumvitSet-text color-graye6e">
                                {t("detail.highest")}
                              </div>
                            </div>
                          </div>
                          <div className="high__box">
                            <img
                              src={imgList.ico_avg}
                              alt=""
                              className="high__img"
                            />
                            <div className="high__detail">
                              <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                                {tempCoilStats.count > 0 ? (
                                  <>
                                    {toFixed(tempCoilStats.avg ?? 0, 1)}
                                    <span className="size-12 font-sukhumvitSet-text color-black">
                                      °{filterTemp.unit === "c" ? "C" : "F"}
                                    </span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </div>
                              <div className="size-16 font-sukhumvitSet-text color-graye6e">
                                {t("detail.average")}
                              </div>
                            </div>
                          </div>
                        </Highlight2>
                      </TempBox>
                      {/* <Highlight className="high temp">
                        <div className="high__box">
                          <img
                            src={imgList.ico_low}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-16 font-sukhumvitSet-text color-graye6e">
                              Lowest (Room Temp/ Evap Temp)
                            </div>
                            <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataTempRaw?.results ?? []).length > 0
                                ? toFixed(
                                    _.orderBy(
                                      dataTempRaw?.results ?? [],
                                      "insideTemp",
                                      "asc"
                                    )?.[0]?.["insideTemp"] ?? 0,
                                    1
                                  )
                                : "-"}
                              <span className="size-12 font-sukhumvitSet-text color-black">
                                °{filterTemp.unit === "c" ? "C" : "F"}
                              </span>
                              <span className="between-span">/</span>
                              {(dataTempRaw?.results ?? []).length > 0 &&
                              (_.orderBy(
                                dataTempRaw?.results ?? [],
                                "evapCoil",
                                "asc"
                              )?.[0]?.["evapCoil"] ?? 0) !== 0 ? (
                                <>
                                  {toFixed(
                                    _.orderBy(
                                      dataTempRaw?.results ?? [],
                                      "evapCoil",
                                      "asc"
                                    )?.[0]?.["evapCoil"] ?? 0,
                                    1
                                  )}
                                  <span className="size-12 font-sukhumvitSet-text color-black">
                                    °{filterTemp.unit === "c" ? "C" : "F"}
                                  </span>
                                </>
                              ) : (
                                "-"
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="high__box">
                          <img
                            src={imgList.ico_high}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-16 font-sukhumvitSet-text color-graye6e">
                              Highest (Room Temp/ Evap Temp)
                            </div>
                            <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataTempRaw?.results ?? []).length > 0
                                ? toFixed(
                                    _.orderBy(
                                      dataTempRaw?.results ?? [],
                                      "insideTemp",
                                      "desc"
                                    )?.[0]?.["insideTemp"] ?? 0,
                                    1
                                  )
                                : "-"}
                              <span className="size-12 font-sukhumvitSet-text color-black">
                                °{filterTemp.unit === "c" ? "C" : "F"}
                              </span>
                              <span className="between-span">/</span>
                              {(dataTempRaw?.results ?? []).length > 0 &&
                              (_.orderBy(
                                dataTempRaw?.results ?? [],
                                "evapCoil",
                                "desc"
                              )?.[0]?.["evapCoil"] ?? 0) !== 0 ? (
                                <>
                                  {toFixed(
                                    _.orderBy(
                                      dataTempRaw?.results ?? [],
                                      "evapCoil",
                                      "desc"
                                    )?.[0]?.["evapCoil"] ?? 0,
                                    1
                                  )}
                                  <span className="size-12 font-sukhumvitSet-text color-black">
                                    °{filterTemp.unit === "c" ? "C" : "F"}
                                  </span>
                                </>
                              ) : (
                                "-"
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="high__box">
                          <img
                            src={imgList.ico_avg}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-16 font-sukhumvitSet-text color-graye6e">
                              Average (Room Temp/ Evap Temp)
                            </div>
                            <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataTempRaw?.results ?? []).length > 0
                                ? toFixed(
                                    _.meanBy(
                                      dataTempRaw?.results ?? [],
                                      (p: any) => p?.insideTemp ?? 0
                                    ),
                                    1
                                  )
                                : "-"}
                              <span className="size-12 font-sukhumvitSet-text color-black">
                                °{filterTemp.unit === "c" ? "C" : "F"}
                              </span>
                              <span className="between-span">/</span>
                              {(dataTempRaw?.results ?? []).length > 0 &&
                              _.meanBy(
                                dataTempRaw?.results ?? [],
                                (p: any) => p?.evapCoil ?? 0
                              ) !== 0 ? (
                                <>
                                  {toFixed(
                                    _.meanBy(
                                      dataTempRaw?.results ?? [],
                                      (p: any) => p?.evapCoil ?? 0
                                    ),
                                    1
                                  )}
                                  <span className="size-12 font-sukhumvitSet-text color-black">
                                    °{filterTemp.unit === "c" ? "C" : "F"}
                                  </span>
                                </>
                              ) : (
                                "-"
                              )}
                            </div>
                          </div>
                        </div>
                      </Highlight> */}
                      {/* data-presence loading (independent of the global
                          loading backdrop): placeholder until the report's rows
                          arrive, then the chart */}
                      {Array.isArray(dataTempNew?.results) ? (
                        <>
                          {/* hours/days at a coarser resolution plot the same
                              downsampled rows as the table; the weekly/monthly
                              chart keeps the S3 per-day file (by design) and
                              1-min keeps the legacy gap-pruned grid */}
                          <ChartNewTemperatureSum
                            data={
                              tempResolution > 1 &&
                              filterDeviceDetail.unit !== "weeks" &&
                              filterDeviceDetail.unit !== "months"
                                ? tempDisplayRows
                                : dataTempNew?.results ?? []
                            }
                          />
                          {/* <TemperatureAmchart datas={dataTempNew?.results ?? []} /> */}
                        </>
                      ) : (
                        <ChartPlaceholder
                          loading
                          loadingText={t("detail.loadingdata")}
                          emptyText={t("detail.nodatarange")}
                        />
                      )}
                    </div>
                  )}
                  {tabReport === 2 && (
                    <div className="detail__card detail__card--margin">
                      <div className="detail__card-head">
                        <div className="detail__card-name">
                          <img src={imgList.ico_operation} alt="" />
                          <div className="size-20 weight-700 font-sukhumvitSet-text color-black">
                            {t("detail.ope")}
                          </div>
                        </div>
                        <div className="detail__card-filter">
                          <img
                            src={imgList.ico_filter}
                            alt=""
                            className="w-50"
                            onClick={() =>
                              setModal({ open: "filter_oparetion" })
                            }
                          />
                          {/* labeled pill instead of a bare arrow — matches the
                              temperature card so customers get the scent trail to
                              the full operation report page (Excel download) */}
                          <div
                            className="detail__card-report last"
                            onClick={() =>
                              navigate(`/device-detail/${id}/operation`)
                            }
                          >
                            <div className="size-12 weight-600 font-sukhumvitSet-text color-black">
                              {t("detail.reportexcelbtn")}
                            </div>
                            <img src={imgList.ico_arrow_yellow} alt="" />
                          </div>
                        </div>
                      </div>
                      <Highlight className="high operation">
                        <div className="high__box">
                          <img
                            src={imgList.ico_ice}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-16 font-sukhumvitSet-text color-graye6e">
                              {t("detail.comp")}
                            </div>
                            <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataOperationRaw?.results ?? []).length > 0
                                ? dataOperationReport?.compressorPeriodUseInHour ??
                                  ""
                                : "-"}{" "}
                              <span className="size-12 font-sukhumvitSet-text color-black">
                                {t("detail.hrs")}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="high__box">
                          <img
                            src={imgList.ico_water_2}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-16 font-sukhumvitSet-text color-graye6e">
                              {t("detail.defrost")}
                            </div>
                            <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataOperationRaw?.results ?? []).length > 0
                                ? dataOperationReport?.defrostPeriodUseInHour ??
                                  ""
                                : "-"}{" "}
                              <span className="size-12 font-sukhumvitSet-text color-black">
                                {t("detail.hrs")}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="high__box">
                          <img
                            src={imgList.ico_fan_2}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-16 font-sukhumvitSet-text color-graye6e">
                              {t("detail.fan")}
                            </div>
                            <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataOperationRaw?.results ?? []).length > 0
                                ? dataOperationReport?.fanPeriodUseInHour ?? ""
                                : "-"}{" "}
                              <span className="size-12 font-sukhumvitSet-text color-black">
                                {t("detail.hrs")}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="high__box">
                          <img
                            src={imgList.ico_hour}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-16 font-sukhumvitSet-text color-graye6e">
                              {t("detail.door")}
                            </div>
                            <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataOperationRaw?.results ?? []).length > 0
                                ? dataOperationReport?.doorOpenTime ?? ""
                                : "-"}{" "}
                              <span className="size-12 font-sukhumvitSet-text color-black">
                                {t("detail.times")}{" "}
                                <span className="size-12 font-sukhumvitSet-text color-gray797">
                                  {dataOperationReport?.doorOpenPeriodInHour ??
                                  ""
                                    ? `(${
                                        (dataOperationRaw?.results ?? [])
                                          .length > 0
                                          ? `${
                                              dataOperationReport?.doorOpenPeriodInHour ??
                                              ""
                                            } ${t("detail.hrs")}`
                                          : "-"
                                      })`
                                    : ""}
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </Highlight>
                      {/* <ChartOperation data={dataOperation?.results ?? []} /> */}
                      {Array.isArray(dataOperationNew?.results) ? (
                        <ChartNewOperation
                          isEvent={filterOpe.recordType !== "record"}
                          data={
                            dataOperationNew?.[
                              filterOpe.recordType === "record"
                                ? "results"
                                : "eventResults"
                            ] ?? []
                          }
                        />
                      ) : (
                        <ChartPlaceholder
                          loading
                          loadingText={t("detail.loadingdata")}
                          emptyText={t("detail.nodatarange")}
                        />
                      )}
                    </div>
                  )}
                  {tabReport === 3 && (
                    <div className="detail__card detail__card--margin">
                      <div className="detail__card-head">
                        <div className="detail__card-name">
                          <img src={imgList.ico_power} alt="" />
                          <div className="size-20 weight-700 font-sukhumvitSet-text color-black">
                            {t("detail.power")}
                          </div>
                        </div>
                        <div className="detail__card-filter">
                          {/* <img
                    src={imgList.ico_filter}
                    alt=""
                    className="w-50"
                    onClick={() => setModal({ open: "filter_power" })}
                  /> */}
                          {/* labeled pill instead of a bare arrow — matches the
                              temperature card so customers get the scent trail to
                              the full power report page (Excel download) */}
                          <div
                            className="detail__card-report last"
                            onClick={() =>
                              navigate(`/device-detail/${id}/power`)
                            }
                          >
                            <div className="size-12 weight-600 font-sukhumvitSet-text color-black">
                              {t("detail.reportexcelbtn")}
                            </div>
                            <img src={imgList.ico_arrow_yellow} alt="" />
                          </div>
                        </div>
                      </div>
                      <Highlight className="high operation">
                        <div className="high__box">
                          <img
                            src={imgList.ico_elec}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-14 font-sukhumvitSet-text color-graye6e">
                              {t("detail.electunit")}
                            </div>
                            <div className="size-18 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataPowerRaw?.results ?? []).length > 0
                                ? toFixed(
                                    dataPowerReport?.electricityUnit ?? 0,
                                    2
                                  )
                                : "-"}{" "}
                              <span className="size-10 font-sukhumvitSet-text color-black">
                                {t("detail.unit2")}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="high__box">
                          <img
                            src={imgList.ico_bill}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-14 font-sukhumvitSet-text color-graye6e">
                              {t("detail.electbill")}
                            </div>
                            <div className="size-18 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataPowerRaw?.results ?? []).length > 0
                                ? toFixed(
                                    dataPowerReport?.electricityBill ?? 0,
                                    2
                                  )
                                : "-"}{" "}
                              <span className="size-10 font-sukhumvitSet-text color-black">
                                {t("detail.baht")}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="high__box">
                          <img
                            src={imgList.ico_carbon}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-14 font-sukhumvitSet-text color-graye6e">
                              {t("detail.carbonfootprint")}
                            </div>
                            <div className="size-18 weight-700 font-sukhumvitSet-bold color-black">
                              {(dataPowerRaw?.results ?? []).length > 0
                                ? toFixed(
                                    dataPowerReport?.electricityCarbon ?? 0,
                                    2
                                  )
                                : "-"}{" "}
                              <span className="size-10 font-sukhumvitSet-text color-black">
                                {t("detail.carbonunit")}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* <div className="high__box">
                  <img src={imgList.ico_high} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-14 font-sukhumvitSet-text color-graye6e">
                      Maximum unit
                    </div>
                    <div className="size-18 weight-700 font-sukhumvitSet-bold color-black">
                      {(dataPowerRaw?.results ?? []).length > 0
                        ? toFixed(dataPowerReport?.electricityMaxUnit ?? 0, 2)
                        : "-"}{" "}
                      <span className="size-10 font-sukhumvitSet-text color-black">
                        unit
                      </span>
                    </div>
                  </div>
                </div>
                <div className="high__box">
                  <img src={imgList.ico_low} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-14 font-sukhumvitSet-text color-graye6e">
                      Minimum unit
                    </div>
                    <div className="size-18 weight-700 font-sukhumvitSet-bold color-black">
                      {(dataPowerRaw?.results ?? []).length > 0
                        ? toFixed(dataPowerReport?.electricityMinUnit ?? 0, 2)
                        : "-"}{" "}
                      <span className="size-10 font-sukhumvitSet-text color-black">
                        unit
                      </span>
                    </div>
                  </div>
                </div>
                <div className="high__box">
                  <img src={imgList.ico_mode} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-14 font-sukhumvitSet-text color-graye6e">
                      Average unit
                    </div>
                    <div className="size-18 weight-700 font-sukhumvitSet-bold color-black">
                      {(dataPowerRaw?.results ?? []).length > 0
                        ? toFixed(dataPowerReport?.electricityAVGUnit ?? 0, 2)
                        : "-"}{" "}
                      <span className="size-10 font-sukhumvitSet-text color-black">
                        unit
                      </span>
                    </div>
                  </div>
                </div> */}
                        <div className="high__box">
                          <img
                            src={imgList.ico_avg}
                            alt=""
                            className="high__img"
                          />
                          <div className="high__detail">
                            <div className="size-14 font-sukhumvitSet-text color-graye6e">
                              {t("detail.mode")}
                            </div>
                            <div className="size-18 weight-700 font-sukhumvitSet-bold color-black">
                              {t(`monitoring.${dataPowerReport?.mode ?? ""}`)}
                            </div>
                          </div>
                        </div>
                      </Highlight>
                      <div className="detail__row-chart">
                        {/* <ChartPower data={dataPower?.results ?? []} type="kw" />
                <ChartPower data={dataPower?.results ?? []} type="voltage" />
                <ChartPower data={dataPower?.results ?? []} type="power" />
                <ChartPower data={dataPower?.results ?? []} type="current" /> */}
                        {Array.isArray(dataPowerNew?.results) ? (
                          <>
                            {/* <div className="large">
                      <ChartNewPower
                        data={dataPowerNew?.results ?? []}
                        type="kw"
                      />
                    </div> */}
                            {/* one chart per row (`large` = full width) and in
                                the same order as the power detail page —
                                voltage, current, power. Half-width side-by-side
                                left the two lower charts cramped. */}
                            <div className="large">
                              <ChartNewPower
                                data={dataPowerNew?.results ?? []}
                                type="voltage"
                              />
                            </div>
                            <div className="large">
                              <ChartNewPower
                                data={dataPowerNew?.results ?? []}
                                type="current"
                              />
                            </div>
                            <div className="large">
                              <ChartNewPower
                                data={dataPowerNew?.results ?? []}
                                type="power"
                              />
                            </div>
                          </>
                        ) : (
                          <ChartPlaceholder
                            loading
                            loadingText={t("detail.loadingdata")}
                            emptyText={t("detail.nodatarange")}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {tabReport === 4 && (
                    <div className="detail__card detail__card--margin">
                      <div className="detail__card-head">
                        <div className="detail__card-name">
                          <img src={imgList.btn_iot_report_head} alt="" />
                          <div className="size-20 weight-700 font-sukhumvitSet-text color-black">
                            {t("detail.iothead")}:{" "}
                            {(dataReportDetail?.ssid ?? "") === "" ||
                            (dataReportDetail?.ssid ?? "") === null
                              ? "-"
                              : dataReportDetail?.ssid}
                          </div>
                        </div>
                        <div className="detail__card-filter">
                          {/* <img
                    src={imgList.ico_filter}
                    alt=""
                    className="w-50"
                    onClick={() => setModal({ open: "filter_iot" })}
                  /> */}
                          {/* labeled pill instead of a bare arrow — matches the
                              temperature card so customers get the scent trail to
                              the full IoT report page (Excel download) */}
                          <div
                            className="detail__card-report last"
                            onClick={() => navigate(`/device-detail/${id}/iot`)}
                          >
                            <div className="size-12 weight-600 font-sukhumvitSet-text color-black">
                              {t("detail.reportexcelbtn")}
                            </div>
                            <img src={imgList.ico_arrow_yellow} alt="" />
                          </div>
                        </div>
                      </div>
                      {/* <Highlight className="high iot">
                <div className="high__box">
                  <img src={imgList.ico_elec} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-16 font-sukhumvitSet-text color-graye6e">
                      Offline Device
                    </div>
                    <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                      {toFixed(dataPower?.electricityUnit ?? 0, 2)}{" "}
                      <span className="size-12 font-sukhumvitSet-text color-black">
                        unit
                      </span>
                    </div>
                  </div>
                </div>
                <div className="high__box">
                  <img src={imgList.ico_bill} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-16 font-sukhumvitSet-text color-graye6e">
                      Max. offline
                    </div>
                    <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                      {toFixed(dataPower?.electricityBill ?? 0, 2)}{" "}
                      <span className="size-12 font-sukhumvitSet-text color-black">
                        Baht
                      </span>
                    </div>
                  </div>
                </div>
                <div className="high__box">
                  <img src={imgList.ico_high} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-16 font-sukhumvitSet-text color-graye6e">
                      MTTR
                    </div>
                    <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                      {toFixed(dataPower?.electricityMaxUnit ?? 0, 2)}{" "}
                      <span className="size-12 font-sukhumvitSet-text color-black">
                        unit
                      </span>
                    </div>
                  </div>
                </div>
                <div className="high__box">
                  <img src={imgList.ico_low} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-16 font-sukhumvitSet-text color-graye6e">
                      Lowest (Rssi)
                    </div>
                    <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                      {toFixed(dataPower?.electricityMinUnit ?? 0, 2)}{" "}
                      <span className="size-12 font-sukhumvitSet-text color-black">
                        unit
                      </span>
                    </div>
                  </div>
                </div>
                <div className="high__box">
                  <img src={imgList.ico_mode} alt="" className="high__img" />
                  <div className="high__detail">
                    <div className="size-16 font-sukhumvitSet-text color-graye6e">
                      Highest (Rssi)
                    </div>
                    <div className="size-22 weight-700 font-sukhumvitSet-bold color-black">
                      {toFixed(dataPower?.electricityAVGUnit ?? 0, 2)}{" "}
                      <span className="size-12 font-sukhumvitSet-text color-black">
                        unit
                      </span>
                    </div>
                  </div>
                </div>
              </Highlight> */}
                      {Array.isArray(dataIotNew?.wifiResults) ? (
                        <>
                          <ChartNewIot data={dataIotNew?.wifiResults ?? []} />
                          <ChartNewRssi data={dataIotNew?.rssiResults ?? []} />
                        </>
                      ) : (
                        <ChartPlaceholder
                          loading
                          loadingText={t("detail.loadingdata")}
                          emptyText={t("detail.nodatarange")}
                        />
                      )}
                      {/* <ChartIot data={dataIot?.results ?? []} />
              <ChartRssi data={dataIot?.results ?? []} /> */}
                    </div>
                  )}
                  {tabReport === 5 && (
                    <div className="detail__card detail__card--margin">
                      <div className="detail__card-head">
                        <div className="detail__card-name">
                          <img
                            src={imgList.btn_wifievent_report_disable}
                            alt=""
                          />
                          <div className="size-20 weight-700 font-sukhumvitSet-text color-black">
                            {t("detail.wifieventtitle")}
                          </div>
                        </div>
                      </div>
                      {/* the module's reset codes ride on the operation
                          report's raw event rows (see toWifiEvents) */}
                      {Array.isArray(dataWifiEvents) ? (
                        <WifiEventTimeline
                          events={toWifiEvents(dataWifiEvents)}
                        />
                      ) : (
                        <ChartPlaceholder
                          loading
                          loadingText={t("detail.loadingdata")}
                          emptyText={t("detail.nodatarange")}
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                // <div className="detail__comming">
                //   <Coming />
                // </div>
                <TableWarp>
                  <Table
                    scroll={{ x: true }}
                    pagination={{
                      ...tablePagination,
                      pageSize: logsPageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ["20", "50", "100"],
                      // Searching hands every matching row to the table, so it
                      // pages them itself; otherwise each page is a fetch.
                      ...(isSearching
                        ? {
                            total: logRows.length,
                            onShowSizeChange: (_p: number, ps: number) =>
                              setLogsPageSize(ps),
                          }
                        : {
                            current: logsPage,
                            total: logsTotal,
                            // a page-size change re-fetches from page 1 at the
                            // new size; a page change keeps the size
                            onChange: (p: number, ps: number) =>
                              getDeviceLogs(ps !== logsPageSize ? 1 : p, ps),
                          }),
                    }}
                    showSorterTooltip={false}
                    columns={columns}
                    dataSource={logRows.map(
                      (e: any, index: number) => {
                        return {
                          key: index,
                          createdAt: dayjs(e?.createdAt ?? "").format(
                            "DD/MM/YYYY HH:mm:ss"
                          ),
                          activityName: e?.activityName ?? "-",
                          verify: e?.verify ?? false,
                          activityValue: e?.activityValue ?? "-",
                          type: e?.type ?? "-",
                          // actionBy: _.isNull(e?.actionBy)
                          //   ? "Device"
                          //   : (e?.actionBy?.user_fullname ?? "") !== ""
                          //   ? e?.actionBy?.user_fullname ?? ""
                          //   : e?.actionBy?.email ?? "-",
                          actionBy: renderActionBy(e),
                        };
                      }
                    )}
                  />
                </TableWarp>
              )}
            </>
          )}
        </div>
      ) : (
        // before the first report fetch resolves (initData still running) the
        // whole content is otherwise a bare gap under the global spinner — show
        // a skeleton of the tabs + report card so the layout is preserved
        <div className="detail__content">
          <ReportSkeleton />
        </div>
      )}
      <ModalDeviceDetail
        open={modal.open === "device_detail"}
        data={dataReportDetail}
        name={deviceDetail?.deviceInfo?.name ?? "-"}
        onOk={() => {}}
        onClose={() =>
          setModal({
            ...modal,
            open: "",
          })
        }
        onZoomImage={(e) => setZoomImage(e)}
      />
      <ModalZoomImage
        open={zoomImage !== ""}
        image={zoomImage}
        onClose={() => setZoomImage("")}
      />
      <ModalFilterTemp
        open={modal.open === "filter_temp"}
        onOk={() => {
          setModal({
            ...modal,
            open: "",
          });
          // handleFilterTemp();
        }}
        onClose={() =>
          setModal({
            ...modal,
            open: "",
          })
        }
        hideType={true}
      />
      <ModalFilterOperation
        open={modal.open === "filter_oparetion"}
        onOk={() => {
          setModal({
            ...modal,
            open: "",
          });
          // handleFilterTemp();
        }}
        onClose={() =>
          setModal({
            ...modal,
            open: "",
          })
        }
      />
      <ModalFilterIot
        open={modal.open === "filter_iot"}
        onOk={() => {
          setModal({
            ...modal,
            open: "",
          });
          handleFilterIot();
        }}
        onClose={() =>
          setModal({
            ...modal,
            open: "",
          })
        }
      />
      <ModalFilterPower
        open={modal.open === "filter_power"}
        onOk={() => {
          setModal({
            ...modal,
            open: "",
          });
          handleFilterPower();
        }}
        onClose={() =>
          setModal({
            ...modal,
            open: "",
          })
        }
      />
    </Container>
  );
}

interface IStyleProgress {
  width: number;
  percent: number;
  spColor: boolean;
}

interface IStyleTextDatePick {
  disabled: boolean;
}

const ErrorStarter = styled.div`
  width: 100%;
  .error {
    &__image {
      margin: 0 auto 16px;
    }
    &__txt-last {
      margin: 10px 0 10px;
    }
    &__link {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const Tab = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  .tab {
    &__btn {
      cursor: pointer;
      width: 74px;
      height: 86px;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      border-radius: 16px;
      margin: 0 8px;
      > img {
        width: 43%;
      }
      > div {
        margin: 4px 0 0;
      }
      &--active {
        width: 84px;
        height: 100px;
        background: linear-gradient(
          180deg,
          rgba(254, 188, 17, 1) 0%,
          rgba(232, 139, 0, 1) 100%
        );
        > img {
          width: 48%;
        }
      }
    }
  }
`;

// Shown in place of the report card when its fetch came back with nothing —
// in practice a 504 from the proxy after the backend spends 60s on a month of
// minute-level rows. It sits in the card so the tabs and the unit/date filters
// above stay usable, which is the whole point: the way out is to narrow the
// range. Note the browser reports that 504 as a CORS error, because the
// proxy's error page carries no Access-Control-Allow-Origin — the request is
// not actually being blocked by CORS.
function ReportError({ onRetry, t }: { onRetry: () => void; t: any }) {
  return (
    <div className="detail__card detail__card--margin">
      <ErrorBox>
        <div className="err__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.24 3.957 1.482 18.043A2 2 0 0 0 3.24 21h17.52a2 2 0 0 0 1.758-2.957L13.76 3.957a2 2 0 0 0-3.52 0z" />
          </svg>
        </div>
        <div className="size-16 weight-600 font-sukhumvitSet-text color-gray333">
          {t("detail.reportfailed")}
        </div>
        <div className="err__detail size-14 font-sukhumvitSet-text color-gray67e">
          {t("detail.reportfaileddetail")}
        </div>
        <button type="button" className="err__retry" onClick={onRetry}>
          {t("detail.reportretry")}
        </button>
      </ErrorBox>
    </div>
  );
}

// Skeleton of the metric tabs + report card, shown while initData is still
// running (init === false). Reuses the real Tab/detail__card layout so the
// page keeps its shape (no bare gap under the global spinner) and the shimmer
// reads as "content loading" rather than "empty". antd Skeleton.Button `block`
// stretches to its flex parent; fixed px on the tab/badge pieces match the
// real chrome.
function ReportSkeleton() {
  return (
    <>
      <Tab className="tab">
        {Array.from({ length: REPORT_TAB_COUNT }, (_, i) => i).map((i) => (
          <Skeleton.Button
            key={i}
            active
            style={{
              width: 74,
              height: 86,
              borderRadius: 16,
              margin: "0 8px",
            }}
          />
        ))}
      </Tab>
      <div className="detail__card detail__card--margin">
        <div className="detail__card-head">
          <div className="detail__card-name">
            <Skeleton.Avatar
              active
              shape="square"
              size={40}
              style={{ marginRight: 16, borderRadius: 8 }}
            />
            <Skeleton.Button active style={{ width: 140, height: 24 }} />
          </div>
          <div className="detail__card-filter">
            <Skeleton.Button
              active
              style={{ width: 220, height: 36, borderRadius: 20 }}
            />
          </div>
        </div>
        {/* two summary blocks side by side, then the chart area */}
        <div style={{ display: "flex", gap: 16, margin: "22px 0 0" }}>
          <div style={{ flex: 1 }}>
            <Skeleton.Button active block style={{ height: 96, borderRadius: 12 }} />
          </div>
          <div style={{ flex: 1 }}>
            <Skeleton.Button active block style={{ height: 96, borderRadius: 12 }} />
          </div>
        </div>
        <Skeleton.Button
          active
          block
          style={{ height: 280, borderRadius: 12, marginTop: 18 }}
        />
      </div>
    </>
  );
}

const StarterGate = styled.div`
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  & .gate {
    &__card {
      background: #ffffff;
      border-radius: 16px;
      box-shadow: 0px 3.61513px 18.0757px rgba(224, 224, 224, 0.42);
      padding: 48px 40px;
      max-width: 560px;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }
    &__icon {
      width: 56px;
      margin: 0 0 16px;
    }
    &__detail {
      margin: 10px 0 0;
      line-height: 1.65;
      max-width: 440px;
    }
    &__link {
      margin: 20px 0 0;
      text-decoration: none;
    }
    &__back {
      margin: 18px 0 0;
      padding: 9px 26px;
      border: 1px solid rgba(145, 158, 171, 0.32);
      border-radius: 999px;
      background: none;
      cursor: pointer;
      color: ${colors.gray333};
      &:hover {
        border-color: ${colors.primary2};
      }
    }
  }
`;

const ErrorBox = styled.div`
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  & .err {
    &__icon {
      color: #f59e0b;
      margin: 0 0 12px;
      > svg {
        width: 40px;
        height: 40px;
        stroke-width: 1.6;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    }
    &__detail {
      max-width: 520px;
      margin: 8px 0 0;
      line-height: 1.6;
    }
    &__retry {
      margin: 20px 0 0;
      padding: 9px 26px;
      border: 1px solid ${colors.primary2};
      border-radius: 999px;
      background: none;
      cursor: pointer;
      font-family: "SukhumvitSet-Text", sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: ${colors.gray333};
      &:hover {
        background: ${colors.primary2};
      }
    }
  }
`;

const TableWarp = styled.div`
  max-width: 100%;
  overflow: auto;
`;
const IconCheck = styled.img`
  width: 16px;
`;
const Bagde = styled.div`
  display: inline-block;
  padding: 2px 8px 0px;
  border-radius: 16px;
  background: #f0f9ff;
`;
const TempBox = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  @media ${device.laptop} {
    flex-wrap: wrap;
  }
`;

const Highlight2 = styled.div`
  width: calc(50% - 0.8335vw);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 1.389vw 0;
  background: rgba(254, 188, 17, 0.08);
  border-radius: 0.694vw;
  &.temp {
    > div {
      width: calc(95% / 3);
    }
  }
  &.operation {
    > div {
      width: calc(92% / 4);
    }
  }
  &.power {
    > div {
      width: calc(94% / 6);
    }
  }
  &.iot {
    > div {
      width: calc(95% / 5);
    }
  }
  @media ${device.desktopL} {
    width: calc(50% - 10px);
    margin: 20px 0;
    border-radius: 10px;
  }
  @media ${device.laptop} {
    width: 100%;
    flex-wrap: wrap;
    &.temp {
      > div {
        width: calc(100 - 8px / 3);
      }
    }
    &.operation {
      > div {
        width: calc(50% - 8px);
        margin: 0 0 16px;
      }
    }
    &.power {
      > div {
        width: calc(50% - 8px);
        margin: 0 0 16px;
      }
    }
  }
  @media ${device.mobileLL} {
    &.temp {
      > div {
        width: 100%;
        margin: 0 0 16px;
      }
    }
    &.operation {
      > div {
        width: 100%;
        margin: 0 0 16px;
      }
    }
    &.power {
      > div {
        width: 100%;
        margin: 0 0 16px;
      }
    }
  }
  .high {
    &__title {
      width: calc(100% - 1.111vw -1.111vw) !important;
      padding: 1.111vw 0 0.694vw;
      border-bottom: 1px solid #e4e4e4;
      margin: 0 auto !important;
      @media ${device.desktopL} {
        width: calc(100% - 16px - 16px) !important;
        padding: 16px 0 10px;
      }
    }
    &__box {
      display: flex;
      align-items: flex-start;
      // background: rgba(254, 188, 17, 0.08);
      // border-radius: 0.694vw;
      padding: 1.111vw 1.111vw 0.694vw;
      @media ${device.desktopL} {
        // border-radius: 10px;
        padding: 16px 16px 10px;
      }
    }
    &__img {
      width: 2.361vw;
      display: block;
      margin: 0 0.694vw 0 0;
      @media ${device.desktopL} {
        width: 34px;
        margin: 0 10px 0 0;
      }
    }
    &__detail {
      width: calc(100% - 2.361vw - 0.694vw);
      @media ${device.desktopL} {
        width: calc(100% - 34px - 10px);
      }
    }
  }
`;

const Highlight = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin: 1.389vw 0;
  &.temp {
    > div {
      width: calc(95% / 3);
    }
  }
  &.operation {
    > div {
      width: calc(92% / 4);
    }
  }
  &.power {
    > div {
      width: calc(94% / 6);
    }
  }
  &.iot {
    > div {
      width: calc(95% / 5);
    }
  }
  @media ${device.desktopL} {
    margin: 20px 0;
  }
  @media ${device.laptop} {
    flex-wrap: wrap;
    &.temp {
      > div {
        width: calc(100 - 8px / 3);
      }
    }
    &.operation {
      > div {
        width: calc(50% - 8px);
        margin: 0 0 16px;
      }
    }
    &.power {
      > div {
        width: calc(50% - 8px);
        margin: 0 0 16px;
      }
    }
  }
  @media ${device.mobileLL} {
    &.temp {
      > div {
        width: 100%;
        margin: 0 0 16px;
      }
    }
    &.operation {
      > div {
        width: 100%;
        margin: 0 0 16px;
      }
    }
    &.power {
      > div {
        width: 100%;
        margin: 0 0 16px;
      }
    }
  }
  .high {
    &__box {
      display: flex;
      align-items: flex-start;
      background: rgba(254, 188, 17, 0.08);
      border-radius: 0.694vw;
      padding: 1.111vw 1.111vw 0.694vw;
      @media ${device.desktopL} {
        border-radius: 10px;
        padding: 16px 16px 10px;
      }
    }
    &__img {
      width: 2.361vw;
      display: block;
      margin: 0 0.694vw 0 0;
      @media ${device.desktopL} {
        width: 34px;
        margin: 0 10px 0 0;
      }
    }
    &__detail {
      width: calc(100% - 2.361vw - 0.694vw);
      @media ${device.desktopL} {
        width: calc(100% - 34px - 10px);
      }
    }
  }
`;

const TextDatePick = styled.div<IStyleTextDatePick>`
  position: absolute;
  z-index: 1;
  color: #919eab;
  font-family: "SukhumvitSet-Text";
  font-weight: 400;
  font-size: 0.833vw;
  left: 0.972vw;
  top: -0.556vw;
  background: ${(props) =>
    props.disabled
      ? "linear-gradient(rgba(245, 245, 245, 0.06) 40%, rgb(245, 245, 245) 40%)"
      : "#fff"};
  display: inline-block;
  padding: 0 0.278vw;
  @media ${device.desktopL} {
    padding: 0 4px;
    font-size: 12px;
    left: 14px;
    top: -8px;
  }
`;

const DatePick = styled.div`
  position: relative;
  & .ant-picker {
    width: 100%;
    border-radius: 0.556vw;
    padding: 1.007vw 1.111vw;
    font-size: 1.389vw;
    border: 0.069vw solid rgba(145, 158, 171, 0.32);
    &:hover {
      border-color: rgb(255, 187, 84);
    }
    @media ${device.desktopL} {
      border-radius: 8px;
      padding: 14.5px 16px;
      font-size: 20px;
      border: 1px solid rgba(145, 158, 171, 0.32);
    }
  }
  & .ant-picker-input > input {
    font-size: 0.972vw;
    font-family: SukhumvitSet-Text, sans-serif;
    color: ${colors.gray333};
    @media ${device.desktopL} {
      font-size: 14px;
    }
  }
  & .ant-picker-focused {
    box-shadow: none;
    border-color: rgb(255, 187, 84);
  }
  & .ant-picker-range .ant-picker-active-bar {
    background: rgb(255, 187, 84);
  }
  &
    .ant-picker-cell-in-view.ant-picker-cell-today
    .ant-picker-cell-inner:before {
    border: 1px solid rgb(255, 187, 84);
  }
  & .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
    background: rgb(255, 187, 84);
  }
  & .ant-picker-cell-in-view.ant-picker-cell-in-range:before {
    background: rgba(255, 187, 84, 0.2);
  }
  &
    .ant-picker-cell-in-view.ant-picker-cell-range-end:not(
      .ant-picker-cell-range-end-single
    ):before,
  .ant-picker-cell-in-view.ant-picker-cell-range-start:not(
      .ant-picker-cell-range-start-single
    ):before {
    background: rgba(255, 187, 84, 0.2);
  }
`;

const SwitchWarp = styled.div`
  width: 100%;
  & .react-switch-selector-wrapper {
    background: #f9fafb;
    border-radius: 0.556vw;
  }
  & .react-switch-selector-wrapper::before {
    background: #ffffff;
    border: none;
    box-shadow: 0px 1px 3px rgba(16, 24, 40, 0.1),
      0px 1px 2px rgba(16, 24, 40, 0.06);
    border-radius: 0.556vw;
    height: calc(100% - 0.417vw);
    width: calc(50% - 0.417vw);
    margin-left: 0.208vw;
  }
  @media ${device.desktopL} {
    & .react-switch-selector-wrapper {
      border-radius: 8px;
    }
    & .react-switch-selector-wrapper::before {
      height: calc(100% - 12px);
      width: calc(50% - 12px);
      border-radius: 8px;
      margin-left: 6px;
    }
  }
  .sw {
    &__choose {
      width: 100%;
      height: 3.889vw;
      display: flex;
      align-items: center;
      justify-content: center;
      > img {
        width: 1.528vw;
        margin: 0 0.556vw 0 0;
      }
      @media ${device.desktopL} {
        height: 56px;
        > img {
          width: 22px;
          margin: 0 8px 0 0;
        }
      }
    }
  }
`;

const Progress = styled.div<IStyleProgress>`
  position: relative;
  width: 100%;
  .progress {
    &__progress {
      position: relative;
      float: left;
      text-align: center;
    }
    &__bar-overflow {
      position: relative;
      overflow: hidden;
      width: ${(props) => props.width}px;
      height: ${(props) => props.width / 2}px;
    }
    &__bar {
      position: absolute;
      top: 0;
      left: 0;
      width: ${(props) => props.width}px;
      height: ${(props) => props.width}px;
      border-radius: 50%;
      box-sizing: border-box;
      border: 1.528vw solid
        ${(props) => (props.spColor ? "#FFA500" : "#eaecf0")};
      border-bottom-color: #febc11;
      border-right-color: #febc11;
      transform: rotate(${(props) => 45 + props.percent * 1.8}deg);
      @media ${device.desktopL} {
        border: 22px solid ${(props) => (props.spColor ? "#FFA500" : "#eaecf0")};
        border-bottom-color: #febc11;
        border-right-color: #febc11;
      }
    }
    &__detail {
      position: absolute;
      top: 2.431vw;
      left: 50%;
      transform: translate(-50%, 0);
      @media ${device.desktopL} {
        top: 35px;
      }
    }
    &__avg {
      line-height: 1.2;
      padding: 0.278vw 0.694vw;
      background: #f4f4f4;
      border-radius: 100px;
      @media ${device.desktopL} {
        padding: 4px 10px;
      }
    }
    &__percent {
      margin: -0.139vw 0 -0.139vw;
      @media ${device.desktopL} {
        margin: -2px 0 -2px;
      }
    }
    &__row {
      width: 100%;
      display: flex;
      align-items: flex-start;
      margin: 1.389vw 0 0;
      @media ${device.desktopL} {
        margin: 20px 0 0;
      }
    }
    &__row-detail {
      width: 49%;
      display: flex;
      align-items: flex-start;
      &--2 {
        justify-content: flex-end;
      }
    }
    &__dot {
      width: 0.486vw;
      height: 0.486vw;
      margin: 0 0.417vw 0 0;
      border-radius: 50%;
      &--1 {
        background: #febc11;
      }
      &--2 {
        background: #eaecf0;
      }
      @media ${device.desktopL} {
        width: 7px;
        height: 7px;
        margin: 0 6px 0 0;
      }
    }
    &__row-text {
      max-width: calc(100% - 0.486vw - 0.417vw);
      margin: -0.139vw 0 0;
      > div {
        line-height: 1;
      }
      @media ${device.desktopL} {
        max-width: calc(100% - 7px - 6px);
        margin: -2px 0 0;
      }
    }
  }
`;

const Headline = styled.div`
  width: 100%;
  background: #ffffff;
  border: 0.069vw solid #dedede;
  box-shadow: 0px 0.278vw 0.833vw rgba(205, 205, 205, 0.25);
  border-radius: 1.111vw;
  padding: 1.389vw 0;
  margin: 1.389vw 0 0;
  display: flex;
  // align-items: flex-start;
  @media ${device.desktopL} {
    border: 1px solid #dedede;
    box-shadow: 0px 4px 12px rgba(205, 205, 205, 0.25);
    border-radius: 16px;
    padding: 20px 0;
    margin: 20px 0 0;
  }
  @media ${device.laptop} {
    flex-wrap: wrap;
  }
  .headline {
    &__hr-top {
      position: absolute;
      width: 1px;
      height: 90%;
      top: 0;
      right: 0;
      background: #e4e4e4;
    }
    &__hr-bottom {
      position: absolute;
      width: 1px;
      height: 90% !important;
      bottom: 0;
      right: 0;
      background: #e4e4e4;
    }
    &__left {
      position: relative;
      width: 40%;
      padding: 0 1.389vw;
      display: flex;
      align-items: flex-start;
      border-right: 0.069vw solid #e4e4e4;
      @media ${device.desktopL} {
        padding: 0 20px;
        border-right: 1px solid #e4e4e4;
      }
      @media ${device.laptopSL} {
        width: 39%;
      }
      @media ${device.laptop} {
        width: 50%;
        margin: 0 0 14px;
        border-right: none;
      }
      @media ${device.mobileLL} {
        width: 100%;
      }
    }
    &__middle {
      width: 35%;
      padding: 0 1.389vw;
      border-right: 0.069vw solid #e4e4e4;
      @media ${device.desktopL} {
        padding: 0 20px;
        border-right: 1px solid #e4e4e4;
      }
      @media ${device.laptopSL} {
        width: 33%;
      }
      @media ${device.laptop} {
        width: 50%;
        border-right: none;
        margin: 40px 0 0;
      }
      @media ${device.mobileLL} {
        width: 100%;
        margin: 20px 0 0;
        border-right: none;
      }
    }
    &__hr {
      width: calc(100% - 40px);
      margin: 20px auto 0;
      border-bottom: 1px solid #e4e4e4;
      display: none;
      @media ${device.mobileLL} {
        display: block;
      }
    }
    &__right {
      width: 25%;
      padding: 0 1.389vw;
      @media ${device.desktopL} {
        padding: 0 20px;
      }
      @media ${device.laptopSL} {
        width: 28%;
      }
      @media ${device.laptop} {
        width: 40%;
        margin: 40px auto 0;
      }
      @media ${device.mobileLL} {
        width: 80%;
        margin: 20px auto 0;
      }
    }
    &__map-warp {
      position: relative;
      width: 50%;
      display: none;
      > div {
        height: 100%;
      }
      @media ${device.laptop} {
        display: block;
      }
      @media ${device.mobileLL} {
        width: 100%;
      }
    }
    &__map {
      width: 100%;
      margin: 0 auto;
      padding: 0px 20px;
      display: none;
      > div {
        height: 100%;
      }
      @media ${device.laptop} {
        display: block;
      }
      @media ${device.mobileLL} {
        width: 100%;
        margin: 12px 0 20px;
        > div {
          height: 200px;
        }
      }
    }
    &__cover {
      position: relative;
      width: 7.361vw;
      padding-top: 7.361vw;
      border-radius: 0.556vw;
      margin: 0 1.389vw 0 0;
      overflow: hidden;
      border-radius: 0.556vw;
      > img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        max-width: unset;
        max-height: unset;
        object-fit: cover;
      }
      @media ${device.desktopL} {
        width: 106px;
        padding-top: 106px;
        border-radius: 8px;
        margin: 0 20px 0 0;
        border-radius: 8px;
      }
    }
    &__name {
      max-width: calc(100% - 1.736vw - 0.972vw);
      @media ${device.desktopL} {
        max-width: calc(100% - 25px - 14px);
      }
    }
    &__sound {
      width: 1.736vw;
      display: block;
      margin: 0 0 0 0.972vw;
      &--active {
        cursor: pointer;
      }
      @media ${device.desktopL} {
        width: 25px;
        margin: 0 0 0 14px;
      }
    }
    &__left-detail {
      width: calc(100% - 7.361vw - 1.389vw);
      @media ${device.desktopL} {
        width: calc(100% - 106px - 20px);
      }
    }
    &__row-head {
      width: 100%;
      display: flex;
      align-items: flex-start;
    }
    &__text-group {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin: 0.833vw 0 0;
      @media ${device.desktopL} {
        margin: 12px 0 0;
      }
    }
    &__text {
      width: 48%;
      display: flex;
      align-items: flex-start;
      > img {
        width: 1.111vw;
        margin: 0 0.417vw 0 0;
      }
      > div {
        width: calc(100% - 1.528vw);
      }
      &--sp {
        width: 100%;
      }
      @media ${device.desktopL} {
        > img {
          width: 16px;
          margin: 0 6px 0 0;
        }
        > div {
          width: calc(100% - 22px);
        }
      }
    }
    &__btn {
      display: inline-flex;
      align-items: center;
      background: #ffffff;
      border: 0.139vw solid #ffe6a4;
      box-shadow: 0px 0.278vw 0.972vw rgba(165, 165, 165, 0.08);
      border-radius: 3.472vw;
      padding: 0.278vw 0.694vw;
      margin: 0.833vw 0px 0px;
      cursor: pointer;
      > div {
        line-height: 1.3;
        margin: 1px 0 0;
      }
      > img {
        width: 1.389vw;
        display: block;
        margin: 0 0 0 0.417vw;
      }
      @media ${device.desktopL} {
        border: 2px solid #ffe6a4;
        box-shadow: 0px 4px 14px rgba(165, 165, 165, 0.08);
        border-radius: 50px;
        padding: 4px 10px;
        margin: 12px 0px 0px;
        > div {
          margin: 1px 0 0;
        }
        > img {
          width: 20px;
          margin: 0 0 0 6px;
        }
      }
    }
    &__temp {
      width: 100%;
      background: #ffffff;
      border: 0.069vw solid #efefef;
      box-shadow: 0.208vw 0.208vw 2.083vw rgba(206, 206, 206, 0.5);
      border-radius: 0.833vw;
      padding: 0.417vw 1.667vw;
      display: flex;
      align-items: center;
      justify-content: space-between;
      @media ${device.desktopL} {
        border: 1px solid #efefef;
        box-shadow: 3px 3px 30px rgba(206, 206, 206, 0.5);
        border-radius: 12px;
        padding: 6px 24px;
      }
    }
    &__group {
      width: 100%;
      margin: 1.042vw 0 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      > div {
        width: calc(70% / 7);
        padding-top: calc(70% / 7);
      }
      @media ${device.desktopL} {
        margin: 15px 0 0;
      }
    }
    &__middle-group {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin: 0.556vw 0 0;
      @media ${device.desktopL} {
        margin: 8px 0 0;
      }
    }
    &__middle-left {
      width: 48%;
    }
    &__middle-right {
      width: 48%;
      text-align: right;
    }
    &__middle-text {
      line-height: 1;
      margin: 0.694vw 0 0;
      &--sp {
        margin: 0.278vw 0 0;
      }
      @media ${device.desktopL} {
        margin: 10px 0 0;
        &--sp {
          margin: 4px 0 0;
        }
      }
    }
  }
`;

interface IStyleCon {
  isTab1: boolean;
}

const Container = styled.div<IStyleCon>`
  width: cal(100% + 4.444vw);
  margin: -2.222vw -2.222vw 0;
  @media ${device.desktopL} {
    width: cal(100% + 64px);
    margin: -32px -32px 0;
  }
  .detail {
    &__head {
      border-radius: 2.778vw 0px 0px 0;
      padding: 1.111vw 2.222vw 1.389vw;
      background: #ffffff;
      @media ${device.desktopL} {
        border-radius: 40px 0px 0px 0;
        padding: 16px 32px 20px;
      }
      @media ${device.laptop} {
        border-radius: 0px;
        padding: 20px 32px;
      }
      @media ${device.mobileLL} {
        padding: 18px;
      }
    }
    &__top {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      @media ${device.mobileLL} {
        flex-wrap: wrap;
      }
    }
    &__top-head {
      width: 280px;
      display: flex;
      align-items: center;
      > img {
        width: 2.083vw;
        height: 2.083vw;
        margin: 0 0.833vw 0 0;
        cursor: pointer;
      }
      @media ${device.desktopL} {
        > img {
          width: 30px;
          height: 30px;
          margin: 0 12px 0 0;
        }
      }
    }
    // &__text {
    //   display: flex;
    //   align-items: center;
    //   line-height: 3.75vw;
    //   > img {
    //     width: 1.528vw;
    //     margin: 0 0.208vw 0 0;
    //   }
    //   > span {
    //     margin: 0 0 0 0.139vw;
    //   }
    //   @media ${device.desktopL} {
    //     line-height: 54px;
    //     > span {
    //       margin: 0 0 0 2px;
    //     }
    //     > img {
    //       width: 22px;
    //       margin: 0 3px 0 0;
    //     }
    //   }
    // }
    &__text {
      display: flex;
      align-items: center;
      line-height: 3.75vw;
      > img {
        width: 1.528vw;
        margin: 0 0.208vw 0 0;
      }
      > span {
        margin: 0 0 0 0.139vw;
      }
      @media ${device.desktopL} {
        line-height: 54px;
        > span {
          margin: 0 0 0 2px;
        }
        > img {
          width: 22px;
          margin: 0 3px 0 0;
        }
      }
    }
    &__alert {
      width: 100%;
      padding: 0.972vw 1.111vw;
      background: #c41616;
      box-shadow: 0px 0.278vw 0.972vw rgba(0, 0, 0, 0.08);
      border-radius: 0.833vw;
      margin: 1.389vw 0 0;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      @media ${device.desktopL} {
        padding: 14px 16px;
        box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.08);
        border-radius: 12px;
        margin: 20px 0 0;
      }
    }
    &__alert-left {
      width: 1.111vw;
      margin: -0.069vw 0.486vw 0 0;
      &--hide {
        opacity: 0;
      }
      > img {
        max-width: 100% !important;
      }
      @media ${device.desktopL} {
        width: 16px;
        margin: -1px 7px 0 0;
      }
    }
    &__alert-middle {
      width: 2.361vw;
      margin: 0 0 0 0;
      line-height: 1.3;
      @media ${device.desktopL} {
        width: 34px;
        margin: 0 0 0 0;
      }
    }
    &__alert-right {
      width: calc(100% - 1.25vw - 2.361vw - 0.486vw);
      line-height: 1.3;
      @media ${device.desktopL} {
        width: calc(100% - 18px - 34px - 7px);
      }
    }
    &__filter {
      margin: 0.694vw 0 0;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      &--ismobile {
        display: none;
        margin: 0;
        & .detail__filter-left,
        .detail__search {
          width: 100%;
        }
        & .detail__date {
          margin: 10px 0 0;
          width: 100%;
        }
      }
      @media ${device.desktopL} {
        margin: 10px 0 0;
      }
      @media ${device.mobileLL} {
        &--ismobile {
          &.show {
            display: flex;
          }
        }
      }
    }
    &__filter-left {
      width: ${(props) => (props.isTab1 ? "100%" : "49%")};
      display: flex;
      align-items: center;
      /* space-between stretched three short controls across the whole row.
         Group them at the right instead; the tab switch keeps the left via
         its auto margin. Only on the report tab — the log tab needs its
         switch left with nothing else in this block. */
      justify-content: ${(props) => (props.isTab1 ? "flex-end" : "unset")};
      flex-wrap: wrap;
      @media ${device.mobileLL} {
        width: ${(props) => (props.isTab1 ? "100%" : "calc(100% - 50px)")};
      }
    }
    &__filter-right {
      width: 49%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: wrap;
      /* The log tab packs download + search + range picker in here, which does
         not fit in half the row. Size it to its content and refuse to shrink —
         left to the shared flex rules it gives way to the 49% on its left and
         wraps the range picker onto a second line. */
      &--logs {
        width: auto;
        flex: 0 0 auto;
        flex-wrap: nowrap;
      }
      @media ${device.mobileLL} {
        width: ${(props) => (props.isTab1 ? "49%" : "50px")};
        &--logs {
          width: 50px;
          flex: 0 0 50px;
        }
      }
    }
    &__switch {
      width: 12.778vw;
      margin: 0.694vw ${(props) => (props.isTab1 ? "auto" : "1.528vw")} 0 0;
      @media ${device.desktopL} {
        width: 184px;
        margin: 10px ${(props) => (props.isTab1 ? "auto" : "22px")} 0 0;
      }
      @media ${device.mobileLL} {
        width: 100%;
      }
    }
    &__search {
      width: 20.833vw;
      margin: 0.694vw 0 0;
      /* Sits between the export button and the range picker on the log tab,
         trimmed to the same width so the pair reads as one group rather than
         two controls that happen to be adjacent. */
      &--desktop {
        width: 18.056vw;
        margin-left: 1.389vw;
      }
      @media ${device.desktopL} {
        width: 300px;
        margin: 10px 0 0;
        &--desktop {
          width: 260px;
          margin-left: 20px;
        }
      }
      @media ${device.mobileLL} {
        &--desktop {
          display: none;
        }
      }
    }
    &__date {
      margin: 0.694vw 0 0 1.389vw;
      /* wide enough for "28/07/2026 → 29/07/2026" and no wider */
      &--desktop {
        width: 18.056vw;
      }
      @media ${device.desktopL} {
        margin: 10px 0 0 20px;
        &--desktop {
          width: 260px;
        }
      }
      @media ${device.mobileLL} {
        &--desktop {
          display: none;
        }
      }
    }
    /* Search box and range picker sit side by side on the log tab, and their
       component defaults differ by several pixels — pin both to one height
       rather than chasing each one's padding. Inputs centre a single line of
       text on their own once the vertical padding is out of the way. */
    &__search--desktop .ant-input,
    &__date--desktop .ant-picker {
      height: 3.333vw;
      padding-top: 0;
      padding-bottom: 0;
      @media ${device.desktopL} {
        height: 48px;
      }
    }
    &__download {
      width: 3.472vw;
      height: 3.889vw;
      display: flex;
      align-items: center;
      cursor: pointer;
      margin: 0.694vw 0 0;
      &--disabled {
        cursor: not-allowed;
        filter: grayscale(1);
      }
      > img {
        width: 100%;
        display: block;
      }
      @media ${device.desktopL} {
        width: 50px;
        height: 56px;
        margin: 10px 0 0;
      }
    }
    &__card {
      width: 100%;
      padding: 1.389vw;
      background: #ffffff;
      box-shadow: 0px 0.251vw 1.255vw rgba(224, 224, 224, 0.42);
      border-radius: 1.111vw;
      &--margin {
        margin: 1.667vw 0 0;
      }
      @media ${device.desktopL} {
        &--margin {
          margin: 24px 0 0;
        }
        padding: 20px;
        box-shadow: 0px 3.61513px 18.0757px rgba(224, 224, 224, 0.42);
        border-radius: 16px;
      }
    }
    &__period {
      /* was a third of the leftover row each, which is far wider than "ชั่วโมง",
         a date and a time range need */
      width: 13.889vw;
      margin: 0.694vw 0 0 1.389vw;
      /* The three filter controls come from two components with different
         defaults: SelectSimple ships a 56px box, DatePick's picker computes to
         53 out of its padding. Three pixels, but they sit side by side and the
         row reads as slightly out of line. Match the selects to the picker —
         scoped here, since SelectSimple is used across the whole app. */
      & .ant-select-selector {
        height: 3.681vw !important;
      }
      @media ${device.desktopL} {
        width: 200px;
        margin: 10px 0 0 20px;
        & .ant-select-selector {
          height: 53px !important;
        }
      }
      @media ${device.mobileLL} {
        width: 100%;
        margin: 16px 0 0;
      }
    }
    &__content {
      padding: 1.389vw 2.222vw;
      @media ${device.desktopL} {
        padding: 20px 32px;
      }
      @media ${device.mobileLL} {
        padding: 18px;
      }
    }
    &__comming {
      display: flex;
      align-items: center;
      width: 100%;
      padding: 3.472vw 0;
      @media ${device.desktopL} {
        padding: 50px 0;
      }
    }
    &__dot {
      width: 0.972vw;
      height: 0.972vw;
      margin: 0px 0.972vw 0px 0px;
      border-radius: 50%;
      @media ${device.desktopL} {
        width: 14px;
        height: 14px;
        margin: 0px 3px 0px 0px;
      }
    }
    &__card-head {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    &__card-name {
      width: 50%;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      > img {
        width: 2.778vw;
        margin: 0 1.111vw 0 0;
        display: block;
      }
      @media ${device.desktopL} {
        > img {
          width: 40px;
          margin: 0 16px 0 0;
        }
      }
    }
    &__card-filter {
      width: 50%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      > img {
        margin: 0 0.972vw 0 0;
        cursor: pointer;
      }
      & .w-50 {
        width: 3.472vw;
      }
      & .w-33 {
        width: 2.292vw;
      }
      & .last {
        margin: 0;
      }
      @media ${device.desktopL} {
        > img {
          margin: 0 14px 0 0;
        }
        & .w-50 {
          width: 50px;
        }
        & .w-33 {
          width: 33px;
        }
      }
    }
    /* labeled nav pill in a metric-card header (same look as headline__btn) —
       tells customers the full report + PDF/Excel downloads live one click away */
    &__card-report {
      display: inline-flex;
      align-items: center;
      background: #ffffff;
      border: 0.139vw solid #ffe6a4;
      box-shadow: 0px 0.278vw 0.972vw rgba(165, 165, 165, 0.08);
      border-radius: 3.472vw;
      padding: 0.278vw 0.833vw;
      cursor: pointer;
      white-space: nowrap;
      > div {
        line-height: 1.3;
        margin: 1px 0 0;
      }
      > img {
        width: 1.389vw;
        display: block;
        margin: 0 0 0 0.417vw;
      }
      @media ${device.desktopL} {
        border: 2px solid #ffe6a4;
        box-shadow: 0px 4px 14px rgba(165, 165, 165, 0.08);
        border-radius: 50px;
        padding: 4px 12px;
        > img {
          width: 20px;
          margin: 0 0 0 6px;
        }
      }
    }
    &__row-chart {
      width: 100%;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      flex-wrap: wrap;
      & .large {
        width: 100%;
      }
      > div {
        width: 48%;
      }
      @media ${device.mobileLL} {
        > div {
          width: 100%;
        }
      }
    }
    &__lastupdate {
      /* rides along the breadcrumb row, pushed to the far right */
      display: flex;
      align-items: center;
      margin-left: auto;
      white-space: nowrap;
      /* the gap — not the {" "} in the JSX — is what separates the label from
         the date: a flex container drops any child that is only whitespace, so
         the space after the colon would never render on its own */
      gap: 0.278vw;
      > img {
        width: 1.111vw;
        margin: 0 0.139vw 0 0;
      }
      @media ${device.desktopL} {
        gap: 4px;
        > img {
          width: 16px;
          margin: 0 2px 0 0;
        }
      }
      @media ${device.mobileLL} {
        /* wrapped onto its own line — no auto margin to fight the wrap */
        margin-left: 0;
      }
    }
    &__breadclumb {
      width: 100%;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      row-gap: 4px;
      margin: 0.278vw 0 0;
      /* line up with the TITLE text, not the back arrow: the arrow (30px + 12px
         margin) indents the title, so an unindented breadcrumb left the two
         lines on different left edges */
      padding-left: 2.916vw;
      @media ${device.desktopL} {
        padding-left: 42px;
      }
      & .pointer {
        cursor: pointer;
      }
      & .sep {
        color: #919eab;
        margin: 0 0.556vw;
      }
      & .active {
        color: #919eab;
      }
      @media ${device.desktopL} {
        margin: 4px 0 0;
        & .sep {
          margin: 0 8px;
        }
      }
    }
    &__hr-2 {
      width: 100%;
      margin: 32px 0;
      background-color: #e4e4e4;
      height: 2px;
      border: 0;
    }
    &__table-report {
      margin: 16px 0 0;
    }
  }
`;
